import { LectureRecordClient } from './services/lecture-records';
import { CurrentGamesPanel } from './components/CurrentGamesPanel';
import { RoomInvitation } from './components/RoomInvitation';
import { EbooksPage } from './components/EbooksPage';
import { EbookClient } from './services/ebooks';
import {
  ArrowLeftIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type {
  BoardPoint,
  CreatorColorPreference,
  GameType,
  GameHistoryItem,
  StoneColor,
  TimedGameSnapshot,
} from '@wx-weiqi/contracts';

import { GoBoard } from './components/GoBoard';
import { BrandMark } from './components/BrandMark';
import { GameConfirmationDialog } from './components/GameConfirmationDialog';
import {
  boardPointLabel,
  currentMoveDraft,
  unfinishedGame,
  type MoveDraft,
} from './domain/game-interaction';
import './components/student-game.css';
import { LoginScreen } from './components/LoginScreen';
import { FoxKifuPage } from './components/FoxKifuPage';
import { ClassroomLecturePage } from './components/ClassroomLecturePage';
import { GameHistoryReviewPage } from './components/GameHistoryReviewPage';
import { AssignedClassGamesPanel } from './components/AssignedClassGamesPanel';
import { StudentInformationPanel } from './components/StudentInformationPanel';
import { RoleHome } from './components/RoleHome';
import { hideStudentEbookTopbar, showHeaderEbookEntry } from './domain/ebook-entry-presentation';
import { RoleDialogHeading, roleDialogClassName } from './components/RoleDialogHeading';
import { TeacherAiManagementPanel } from './components/TeacherAiManagementPanel';
import { QuestionBankPanel } from './components/QuestionBankPanel';
import { HomeworkAssignmentComposer } from './components/HomeworkManagementPanel';
import { HomeworkAnswerPage } from './components/HomeworkAnswerPage';
import { StudentHomeworkListPanel } from './components/StudentHomeworkListPanel';
import { StudentGameHistoryPanel } from './components/StudentGameHistoryPanel';
import { HomeworkReviewListPanel } from './components/HomeworkReviewListPanel';
import { HomeworkGradingPage } from './components/HomeworkGradingPage';
import { HomeworkAnalyticsPanel } from './components/HomeworkAnalyticsPanel';
import './components/teacher-data-dialogs.css';
import './components/return-navigation.css';
import './components/student-home-shell.css';
import './components/teacher-home-shell.css';
import './components/ebook-student-shell.css';
import {
  StudentHomeworkResultPanel,
  studentHomeworkOpenAction,
} from './components/StudentHomeworkResultPanel';
import { AdminPanel } from './components/AdminPanel';
export { AdminPanel } from './components/AdminPanel';
import type { AssignedGamesView } from './domain/assigned-class-games';
import { runtimeConfig } from './config';
import { advanceClockVoice, initialClockVoiceMemory } from './domain/clock-voice';
import {
  createClockProjectionAnchor,
  formatDuration,
  projectedByoyomi,
  projectedRemainingMs,
  projectedServerNow,
} from './domain/clock-projection';
import { MAIN_TIME_OPTIONS } from './domain/game-creation-options';
import { filterHistoryByDate } from './domain/game-review-presentation';
import { filterGuidedGameStudents } from './domain/guided-game-student-search';
import { pointKey } from './domain/board-geometry';
import { scoringConfirmationLabel, shouldClearScoringDraft } from './domain/scoring-presentation';
import {
  buildTimingComparisonRows,
  filterTimingMoves,
  formatPreciseDuration,
  normalizeTimingRange,
  summarizeTimingRange,
  type TimingMoveColorFilter,
  type TimingMoveDurationFilter,
} from './domain/timing-report';
import { HttpService } from './services/http';
import { announceClock, cancelClockAnnouncements } from './services/clock-voice';
import { GameStore } from './stores/game-store';
import { GameReplayStore } from './stores/game-replay-store';
import { IdentityStore } from './stores/identity-store';
import { AdminStore } from './stores/admin-store';
import { TeachingStore, type TeacherGameFilters } from './stores/teaching-store';
import { FoxKifuStore } from './stores/fox-kifu-store';
import { QuestionBankStore } from './stores/question-bank-store';
import { HomeworkStore } from './stores/homework-store';
import { StudentActivityService, type StudentActivityApi } from './services/student-activity';
import { ServiceStudentPracticeClient } from './services/student-practice-client';
import { StudentRoomMembersCard } from './components/StudentPeopleCards';
import {
  StudentObservationPage,
  StudentWatchGamesPanel,
} from './components/StudentObservationPage';
import { TeacherObservationGamePicker } from './components/TeacherObservationGamePicker';
import {
  TeacherTextbookManagementDialog,
  type TeacherTextbookManagementView,
} from './components/TeacherTextbookManagementDialog';

const STATUS_LABELS: Readonly<Record<TimedGameSnapshot['status'], string>> = {
  CREATED: '等待准备',
  WAITING: '等待准备',
  READY: '等待准备',
  PLAYING: '对弈中',
  COUNTING_REQUESTED: '等待数子确认',
  SCORING: '数子中',
  FINISHED: '已结束',
  ABORTED: '已中止',
};

export const GAME_ERROR_AUTO_DISMISS_MS = 1_500;
const CURRENT_GAME_STATUSES = [
  'CREATED',
  'WAITING',
  'READY',
  'PLAYING',
  'COUNTING_REQUESTED',
  'SCORING',
] as const;

export type GamePrompt =
  | { readonly kind: 'LEAVE' }
  | { readonly kind: 'PASS' }
  | { readonly kind: 'RESIGN' }
  | { readonly kind: 'SUBMIT_SCORING'; readonly points: readonly BoardPoint[] }
  | { readonly kind: 'RESPOND_COUNTING'; readonly countingRequestId: string }
  | { readonly kind: 'RESPOND_TAKEBACK'; readonly sequence: number };

type LobbyDialog =
  | 'CREATE_MENU'
  | 'CREATE'
  | 'CURRENT_GAMES'
  | 'ENTRY'
  | 'JOIN'
  | 'TEACHING_MANAGE'
  | 'CLASS_GAME_CREATE'
  | 'GUIDED_GAME_CREATE'
  | 'CLASS_GAME_VIEW'
  | 'QUESTION_BANK'
  | 'ASSIGNED_GAMES'
  | 'HISTORY'
  | 'HOMEWORK_MENU'
  | 'HOMEWORK_ASSIGN'
  | 'HOMEWORK_REVIEW'
  | 'HOMEWORK_DATA'
  | 'HOMEWORK_STUDENT_RESULTS'
  | 'AI_MANAGEMENT'
  | 'WATCH_GAMES'
  | 'MY_HOMEWORK';
// Student observation is separate from the participant GameStore and its WebSocket commands.

interface TeacherGameListContext {
  readonly scope: 'CLASS' | 'STUDENT';
  readonly filters: TeacherGameFilters;
  readonly title: string;
}

export function gamePromptContent(prompt: GamePrompt) {
  switch (prompt.kind) {
    case 'RESPOND_COUNTING':
      return {
        eyebrow: '数子请求',
        title: '对方申请进入数子',
        detail: '确定数子后进入双方死子标记；继续对弈则恢复当前棋局。',
        cancelLabel: '继续对弈',
        confirmLabel: '确定数子',
        confirmClass: 'primary-button',
      } as const;
    case 'RESPOND_TAKEBACK':
      return {
        eyebrow: '悔棋申请',
        title: `对方申请撤回第 ${prompt.sequence} 手`,
        detail: '同意后服务器将恢复到该手之前；拒绝则保持当前局面。',
        cancelLabel: '拒绝悔棋',
        confirmLabel: '同意悔棋',
        confirmClass: 'primary-button',
      } as const;
    case 'SUBMIT_SCORING':
      return {
        eyebrow: '操作确认',
        title: '确认提交死子标记？',
        detail:
          prompt.points.length === 0
            ? '本方将提交“没有死子”的标记，提交后不能修改。'
            : `本方已选择 ${prompt.points.length} 个棋块标记点，提交后不能修改。`,
        cancelLabel: '取消',
        confirmLabel: '确认提交',
        confirmClass: 'primary-button',
      } as const;
    case 'LEAVE':
      return {
        title: '本局尚未结束，确定返回大厅？',
        detail:
          '离开会断开本局连接，并可能进入断线倒计时；未及时返回可能判负。返回大厅不会结束本局，可从“我的对局列表”选择对局恢复。',
        cancelLabel: '继续对弈',
        confirmLabel: '返回大厅',
        confirmClass: 'secondary-button',
      } as const;
    case 'PASS':
      return {
        title: '确认停一手？',
        detail: '本手不落子，交给对方。连续停一手可能进入数子流程，请确认盘上已无需要争取的地方。',
        cancelLabel: '继续思考',
        confirmLabel: '确认停一手',
        confirmClass: 'primary-button',
      } as const;
    case 'RESIGN':
      return {
        eyebrow: '操作确认',
        title: '确认认输并结束本局？',
        detail: '确认后本局将判你负，结果不能撤销。',
        cancelLabel: '取消',
        confirmLabel: '确认认输',
        confirmClass: 'danger-action',
      } as const;
  }
}

function monotonicNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export function useMonotonicNow(intervalMs = 200): number {
  const [now, setNow] = useState(monotonicNow);
  useEffect(() => {
    const refresh = () => setNow(monotonicNow());
    const timer = window.setInterval(refresh, intervalMs);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
    };
  }, [intervalMs]);
  return now;
}

export function App() {
  const config = useMemo(() => runtimeConfig(window.location), []);
  const [identity] = useState(() => new IdentityStore(new HttpService(config.apiBaseUrl)));
  const [game] = useState(() => new GameStore(identity, config.gameWebSocketUrl));
  const [admin] = useState(
    () =>
      new AdminStore(identity, () => {
        const state = identity.snapshot().session;
        const selected = state?.data.tokens.selectedMembershipId;
        return (
          state?.data.memberships.find((membership) => membership.membershipId === selected)
            ?.organization.organizationId ?? null
        );
      }),
  );
  const [teaching] = useState(
    () =>
      new TeachingStore(
        identity,
        () => {
          const state = identity.snapshot().session;
          const selected = state?.data.tokens.selectedMembershipId;
          return (
            state?.data.memberships.find((membership) => membership.membershipId === selected)
              ?.organization.organizationId ?? null
          );
        },
        () => identity.snapshot().session?.data.tokens.selectedMembershipId ?? null,
      ),
  );
  const [ebooks] = useState(() => new EbookClient(identity));
  const [foxKifu] = useState(() => new FoxKifuStore(identity));
  const [gameReplay] = useState(() => new GameReplayStore(identity));
  const [questionBank] = useState(() => new QuestionBankStore(identity));
  const [homework] = useState(() => new HomeworkStore(identity));
  const [activity] = useState(() => new StudentActivityService(identity));
  const [lectureRecords] = useState(() => new LectureRecordClient(identity));
  const [studentPractice] = useState(() => new ServiceStudentPracticeClient(identity));
  const [observedGameId, setObservedGameId] = useState<string | null>(null);
  const [observationGamePickerOpen, setObservationGamePickerOpen] = useState(false);
  const [resumeWatchList, setResumeWatchList] = useState(false);
  const [section, setSection] = useState<
    | 'EBOOKS'
    | 'LOBBY'
    | 'ADMIN'
    | 'FOX_KIFU'
    | 'CLASSROOM_LECTURE'
    | 'STUDENT_PRACTICE'
    | 'HISTORY_REVIEW'
    | 'HOMEWORK'
    | 'HOMEWORK_REVIEW'
    | 'OBSERVATION'
  >('LOBBY');
  const [studentRankingsOpen, setStudentRankingsOpen] = useState(false);
  useEffect(() => {
    if (section !== 'EBOOKS') setStudentRankingsOpen(false);
  }, [section]);
  const [textbookManagementView, setTextbookManagementView] =
    useState<TeacherTextbookManagementView | null>(null);
  const [reviewHomeworkId, setReviewHomeworkId] = useState<string | null>(null);
  const [resumeHomeworkReview, setResumeHomeworkReview] = useState(false);
  const [teacherGameListContext, setTeacherGameListContext] =
    useState<TeacherGameListContext | null>(null);
  const identityState = useSyncExternalStore(identity.subscribe, identity.snapshot);
  const gameState = useSyncExternalStore(game.subscribe, game.snapshot);
  const teachingState = useSyncExternalStore(teaching.subscribe, teaching.snapshot);
  const identityScope =
    identityState.session === null
      ? null
      : `${identityState.session.data.user.userId}:${identityState.session.data.tokens.selectedMembershipId ?? ''}`;
  const previousIdentityScope = useRef(identityScope);

  useLayoutEffect(() => {
    if (previousIdentityScope.current === identityScope) return;
    previousIdentityScope.current = identityScope;
    game.clear();
    admin.clear();
    teaching.clear();
    foxKifu.clear();
    gameReplay.clear();
    questionBank.clear();
    homework.clear();
    setReviewHomeworkId(null);
    setResumeHomeworkReview(false);
    setTeacherGameListContext(null);
    setObservedGameId(null);
    setObservationGamePickerOpen(false);
    setResumeWatchList(false);
    setStudentRankingsOpen(false);
    setTextbookManagementView(null);
    setSection('LOBBY');
  }, [admin, foxKifu, game, gameReplay, homework, identityScope, questionBank, teaching]);

  const resetAccountWorkspace = () => {
    game.clear();
    admin.clear();
    teaching.clear();
    foxKifu.clear();
    gameReplay.clear();
    questionBank.clear();
    homework.clear();
    setReviewHomeworkId(null);
    setResumeHomeworkReview(false);
    setTeacherGameListContext(null);
    setObservedGameId(null);
    setObservationGamePickerOpen(false);
    setResumeWatchList(false);
    setTextbookManagementView(null);
    setSection('LOBBY');
  };

  const logout = () => {
    resetAccountWorkspace();
    void identity.logout();
  };

  if (identityState.session === null) {
    return (
      <LoginScreen
        identity={identity}
        error={identityState.error}
        pending={identityState.pending}
      />
    );
  }

  if (identityState.session.data.tokens.accessToken === null) {
    return <MembershipScreen identity={identity} onLogout={logout} />;
  }

  const selectedMembership = identityState.session.data.memberships.find(
    (membership) =>
      membership.membershipId === identityState.session?.data.tokens.selectedMembershipId,
  );
  const isPlatformAdmin = identityState.session.data.user.isPlatformAdmin === true;
  const canManage = isPlatformAdmin || selectedMembership?.role === 'ADMIN';
  const useRoleHomePalette =
    section === 'LOBBY' &&
    gameState.snapshot === null &&
    (selectedMembership?.role === 'STUDENT' || selectedMembership?.role === 'TEACHER');
  const useStudentEbookPalette =
    section === 'EBOOKS' && gameState.snapshot === null && selectedMembership?.role === 'STUDENT';
  const hideEbookTopbar = hideStudentEbookTopbar({
    role: selectedMembership?.role,
    section,
    hasGameSnapshot: gameState.snapshot !== null,
  });
  const teacherGameClassName =
    teachingState.teacherGameFilters.classId === undefined
      ? '当前负责班级'
      : (teachingState.classes.find(
          (managedClass) => managedClass.classId === teachingState.teacherGameFilters.classId,
        )?.name ?? '当前班级');
  const activeTeacherGameListContext = teacherGameListContext ?? {
    scope: 'CLASS' as const,
    filters: teachingState.teacherGameFilters,
    title: `${teacherGameClassName}学生棋谱`,
  };

  return (
    <div
      className={`app-frame${useStudentEbookPalette ? ' app-frame--student-ebooks' : ''}${hideEbookTopbar ? ' app-frame--student-ebook-fullscreen' : ''}`}
    >
      {!hideEbookTopbar && (
        <header
          className={
            useRoleHomePalette
              ? `topbar topbar--role-home${selectedMembership?.role === 'STUDENT' ? ' topbar--student-home' : ' topbar--teacher-home'}`
              : useStudentEbookPalette
                ? 'topbar topbar--student-ebook'
                : 'topbar'
          }
        >
          <button
            className="brand"
            type="button"
            onClick={() => {
              game.leave();
              foxKifu.clear();
              gameReplay.clear();
              setTeacherGameListContext(null);
              setReviewHomeworkId(null);
              setSection(canManage ? 'ADMIN' : 'LOBBY');
            }}
            aria-label={canManage ? '返回管理后台' : '返回对弈大厅'}
          >
            <BrandMark />
            <span>
              <strong>弈时围棋</strong>
              <small>
                {canManage
                  ? '平台数据管理'
                  : useRoleHomePalette && selectedMembership?.role === 'TEACHER'
                    ? '教师端'
                    : useRoleHomePalette && selectedMembership?.role === 'STUDENT'
                      ? '让围棋陪伴更好的时光'
                      : '在线对弈平台'}
              </small>
            </span>
          </button>
          {useRoleHomePalette && selectedMembership?.role === 'TEACHER' ? (
            <span className="teacher-home-shell__location" aria-current="page">
              工作台
            </span>
          ) : null}
          {useStudentEbookPalette ? (
            <button
              className="text-button ebook-ranking-entry"
              type="button"
              aria-current={studentRankingsOpen ? 'page' : undefined}
              onClick={() => setStudentRankingsOpen(true)}
            >
              排名
            </button>
          ) : showHeaderEbookEntry({
              role: selectedMembership?.role,
              useRoleHomePalette,
              canManage,
            }) ? (
            <button className="text-button" onClick={() => setSection('EBOOKS')}>
              {selectedMembership?.role === 'TEACHER' ? '我的教材' : '我的电子书'}
            </button>
          ) : null}
          <div className="account-chip">
            <span className="account-avatar">
              {useRoleHomePalette ? (
                <PersonIcon aria-hidden="true" />
              ) : (
                identityState.session.data.user.displayName.slice(0, 1)
              )}
            </span>
            <span>
              <strong>{identityState.session.data.user.displayName}</strong>
              <small>
                {isPlatformAdmin
                  ? '平台管理员'
                  : (selectedMembership?.organization.name ?? '未选择机构')}
              </small>
            </span>
            <button className="text-button" type="button" onClick={logout}>
              退出
            </button>
          </div>
        </header>
      )}

      {gameState.snapshot === null ? (
        section === 'EBOOKS' &&
        (selectedMembership?.role === 'TEACHER' || selectedMembership?.role === 'STUDENT') ? (
          <EbooksPage
            client={ebooks}
            role={selectedMembership.role}
            onBack={() => setSection('LOBBY')}
            rankingsOpen={studentRankingsOpen}
            onRankingsOpen={() => setStudentRankingsOpen(true)}
            onRankingsClose={() => setStudentRankingsOpen(false)}
            teacherNavigation={
              selectedMembership.role === 'TEACHER'
                ? {
                    displayName: identityState.session.data.user.displayName,
                    onHome: () => setSection('LOBBY'),
                    onQuestionBank: () => setTextbookManagementView('questions'),
                    onStudents: () => setTextbookManagementView('students'),
                    onAiManagement: () => setTextbookManagementView('ai'),
                    onLogout: logout,
                  }
                : undefined
            }
          />
        ) : canManage ? (
          <AdminPanel admin={admin} isPlatformAdmin={isPlatformAdmin} />
        ) : section === 'OBSERVATION' &&
          (selectedMembership?.role === 'STUDENT' || selectedMembership?.role === 'TEACHER') &&
          observedGameId !== null ? (
          <StudentObservationPage
            api={activity}
            gameId={observedGameId}
            readTeacherGame={
              selectedMembership.role === 'TEACHER' ? teaching.readTeacherGame : undefined
            }
            replayStore={selectedMembership.role === 'TEACHER' ? gameReplay : undefined}
            onBack={() => {
              gameReplay.clear();
              setObservedGameId(null);
              setObservationGamePickerOpen(false);
              setResumeWatchList(selectedMembership.role === 'STUDENT');
              setSection('LOBBY');
            }}
            onSwitchGame={
              selectedMembership.role === 'TEACHER'
                ? () => setObservationGamePickerOpen(true)
                : undefined
            }
            gamePicker={
              selectedMembership.role === 'TEACHER' && observationGamePickerOpen ? (
                <TeacherObservationGamePicker
                  teaching={teaching}
                  currentGameId={observedGameId}
                  onClose={() => setObservationGamePickerOpen(false)}
                  onSelect={(gameId) => {
                    gameReplay.clear();
                    setObservedGameId(gameId);
                    setObservationGamePickerOpen(false);
                  }}
                />
              ) : undefined
            }
          />
        ) : section === 'FOX_KIFU' && selectedMembership?.role === 'STUDENT' ? (
          <FoxKifuPage
            store={foxKifu}
            onBack={() => {
              foxKifu.clear();
              setSection('LOBBY');
            }}
          />
        ) : section === 'STUDENT_PRACTICE' && selectedMembership?.role === 'STUDENT' ? (
          <ClassroomLecturePage
            store={foxKifu}
            studentPracticeClient={studentPractice}
            teacherName={identityState.session.data.user.displayName}
            onBack={() => {
              foxKifu.clear();
              setSection('LOBBY');
            }}
          />
        ) : section === 'CLASSROOM_LECTURE' && selectedMembership?.role === 'TEACHER' ? (
          <ClassroomLecturePage
            store={foxKifu}
            ebookClient={ebooks}
            lectureRecords={lectureRecords}
            lectureRecordScope={`${selectedMembership.organization.organizationId}:${selectedMembership.membershipId}`}
            teacherName={identityState.session.data.user.displayName}
            onBack={() => {
              foxKifu.clear();
              setSection('LOBBY');
            }}
          />
        ) : section === 'HISTORY_REVIEW' &&
          (selectedMembership?.role === 'STUDENT' || selectedMembership?.role === 'TEACHER') ? (
          <GameHistoryReviewPage
            store={gameReplay}
            title={selectedMembership.role === 'TEACHER' ? '学生棋谱' : '我的棋谱'}
            reportPending={gameState.reportPending}
            teacherToolsEnabled={selectedMembership.role === 'TEACHER'}
            playerGames={gameState.history?.games ?? []}
            playerGameListPending={gameState.historyPending}
            playerGameListError={gameState.error}
            teacherGames={teachingState.teacherGames}
            teacherGameListTitle={activeTeacherGameListContext.title}
            teacherGameListScope={activeTeacherGameListContext.scope}
            teacherGameListPending={teachingState.pending === 'load-teacher-games'}
            teacherGameListError={teachingState.error}
            onBack={() => {
              gameReplay.clear();
              setTeacherGameListContext(null);
              setSection('LOBBY');
            }}
            onRefreshPlayerGames={() => game.loadHistory()}
            onRefreshTeacherGames={() =>
              teaching.loadTeacherGames(activeTeacherGameListContext.filters)
            }
            onSelectGame={(gameId) => gameReplay.load(gameId)}
          />
        ) : section === 'HOMEWORK' && selectedMembership?.role === 'STUDENT' ? (
          <HomeworkAnswerPage
            store={homework}
            activity={activity}
            onBack={() => {
              homework.clearSelection();
              setSection('LOBBY');
            }}
            onComplete={() => {
              homework.clearSelection();
              setSection('LOBBY');
            }}
          />
        ) : section === 'HOMEWORK_REVIEW' &&
          selectedMembership?.role === 'TEACHER' &&
          reviewHomeworkId !== null ? (
          <HomeworkGradingPage
            homeworkId={reviewHomeworkId}
            homework={homework}
            teaching={teaching}
            onBack={() => {
              setReviewHomeworkId(null);
              setResumeHomeworkReview(true);
              setSection('LOBBY');
            }}
          />
        ) : (
          <Lobby
            key={identityScope}
            game={game}
            activity={activity}
            resumeWatchList={resumeWatchList}
            onWatchListResumed={() => setResumeWatchList(false)}
            onWatchGame={(gameId) => {
              gameReplay.clear();
              setResumeWatchList(false);
              setObservedGameId(gameId);
              setSection('OBSERVATION');
            }}
            admin={admin}
            teaching={teaching}
            questionBank={questionBank}
            homework={homework}
            displayName={identityState.session.data.user.displayName}
            role={selectedMembership?.role ?? 'STUDENT'}
            onLogout={logout}
            resumeHomeworkReview={resumeHomeworkReview}
            onHomeworkReviewResumed={() => setResumeHomeworkReview(false)}
            onOpenEbooks={() => setSection('EBOOKS')}
            onOpenFoxKifu={() => setSection('FOX_KIFU')}
            onOpenClassroomLecture={() => {
              foxKifu.clear();
              setSection('CLASSROOM_LECTURE');
            }}
            onOpenStudentPractice={() => {
              foxKifu.clear();
              setSection('STUDENT_PRACTICE');
            }}
            onOpenHomework={(assignmentId) => {
              void homework
                .selectAssignment(assignmentId)
                .then(() => {
                  if (homework.snapshot().selectedAssignment?.assignmentId === assignmentId) {
                    setSection('HOMEWORK');
                  }
                })
                .catch(() => undefined);
            }}
            onOpenHomeworkReview={(homeworkId) => {
              setResumeHomeworkReview(false);
              setReviewHomeworkId(homeworkId);
              setSection('HOMEWORK_REVIEW');
            }}
            onOpenHistoryReview={(gameId, context) => {
              setTeacherGameListContext(context ?? null);
              setSection('HISTORY_REVIEW');
              void gameReplay.load(gameId).catch(() => undefined);
            }}
          />
        )
      ) : (
        <GameRoom
          game={game}
          activity={activity}
          membershipId={identityState.session.data.tokens.selectedMembershipId ?? ''}
          role={selectedMembership?.role ?? ''}
          onReview={(gameId) => {
            game.leave();
            setTeacherGameListContext(null);
            setSection('HISTORY_REVIEW');
            void gameReplay.load(gameId).catch(() => undefined);
          }}
        />
      )}
      {section === 'EBOOKS' &&
      selectedMembership?.role === 'TEACHER' &&
      textbookManagementView !== null ? (
        <TeacherTextbookManagementDialog
          view={textbookManagementView}
          admin={admin}
          teaching={teaching}
          questionBank={questionBank}
          onClose={() => setTextbookManagementView(null)}
          onOpenReview={(gameId, student) => {
            setTextbookManagementView(null);
            setTeacherGameListContext({
              scope: 'STUDENT',
              filters: { studentMembershipId: student.membershipId },
              title: `${student.user.displayName}（${student.accountUsername ?? '微信账号'}）的棋谱`,
            });
            setSection('HISTORY_REVIEW');
            void gameReplay.load(gameId).catch(() => undefined);
          }}
        />
      ) : null}
      {gameState.report !== null ? (
        <TimingReportView
          report={gameState.report}
          moves={gameState.reportMoves}
          closeLabel={gameState.snapshot === null ? '← 返回对局列表' : '← 返回棋局'}
          onClose={() => game.closeTimingReport()}
        />
      ) : null}
    </div>
  );
}

function MembershipScreen({
  identity,
  onLogout,
}: {
  readonly identity: IdentityStore;
  readonly onLogout: () => void;
}) {
  const state = useSyncExternalStore(identity.subscribe, identity.snapshot);
  const session = state.session;
  if (session === null) return null;
  return (
    <main className="center-page">
      <section className="selection-card">
        <p className="eyebrow">账号暂不可用</p>
        <h1>没有可进入的棋院</h1>
        <div className="membership-empty" role="status">
          <strong>当前账号没有有效的机构归属</strong>
          <p>登录权限可能已被管理员停用，请联系管理员核对账号状态。</p>
        </div>
        {state.error !== null ? <p className="message message--error">{state.error}</p> : null}
        <button
          className="secondary-button secondary-button--wide return-button"
          type="button"
          disabled={state.pending}
          onClick={onLogout}
        >
          返回登录
        </button>
      </section>
    </main>
  );
}

export function Lobby({
  game,
  activity,
  onWatchGame,
  resumeWatchList = false,
  onWatchListResumed,
  admin,
  teaching,
  questionBank,
  homework,
  displayName,
  role,
  resumeHomeworkReview,
  onHomeworkReviewResumed,
  onOpenFoxKifu,
  onOpenClassroomLecture,
  onOpenStudentPractice,
  onOpenEbooks,
  onOpenHomework,
  onOpenHomeworkReview,
  onOpenHistoryReview,
  onLogout,
}: {
  readonly game: GameStore;
  readonly activity?: StudentActivityApi | undefined;
  readonly onWatchGame?: ((gameId: string) => void) | undefined;
  readonly resumeWatchList?: boolean;
  readonly onWatchListResumed?: (() => void) | undefined;
  readonly admin: AdminStore;
  readonly teaching: TeachingStore;
  readonly questionBank: QuestionBankStore;
  readonly homework: HomeworkStore;
  readonly displayName: string;
  readonly role: 'STUDENT' | 'TEACHER' | 'ADMIN';
  readonly resumeHomeworkReview: boolean;
  readonly onHomeworkReviewResumed: () => void;
  readonly onOpenFoxKifu: () => void;
  readonly onOpenClassroomLecture: () => void;
  readonly onOpenStudentPractice?: (() => void) | undefined;
  readonly onOpenEbooks?: (() => void) | undefined;
  readonly onOpenHomework: (assignmentId: string) => void;
  readonly onOpenHomeworkReview: (homeworkId: string) => void;
  readonly onOpenHistoryReview: (gameId: string, context?: TeacherGameListContext) => void;
  readonly onLogout?: (() => void) | undefined;
}) {
  const state = useSyncExternalStore(game.subscribe, game.snapshot);
  const teachingState = useSyncExternalStore(teaching.subscribe, teaching.snapshot);
  const questionBankState = useSyncExternalStore(questionBank.subscribe, questionBank.snapshot);
  const homeworkState = useSyncExternalStore(homework.subscribe, homework.snapshot);
  const studentLearning = useSyncExternalStore(
    homework.subscribe,
    homework.studentLearningSnapshot,
  );
  const [boardSize, setBoardSize] = useState<9 | 13 | 19>(19);
  const [mainTimeMs, setMainTimeMs] = useState(1_800_000);
  const [byoyomiPeriodTimeMs, setByoyomiPeriodTimeMs] = useState<
    10_000 | 15_000 | 30_000 | 60_000 | null
  >(null);
  const [byoyomiPeriods, setByoyomiPeriods] = useState<1 | 2 | 3 | 5 | 10>(3);
  const [gameType, setGameType] = useState<GameType>('EVEN');
  const [creatorColor, setCreatorColor] = useState<CreatorColorPreference>('RANDOM');
  const [roomCode, setRoomCode] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [dialog, setDialog] = useState<LobbyDialog | null>(() =>
    resumeWatchList
      ? role === 'TEACHER'
        ? 'CLASS_GAME_VIEW'
        : 'WATCH_GAMES'
      : resumeHomeworkReview
        ? 'HOMEWORK_REVIEW'
        : null,
  );
  useEffect(() => {
    if (!resumeWatchList) return;
    onWatchListResumed?.();
    if (role === 'TEACHER') {
      void teaching
        .loadTeacherDashboard()
        .then(() => teaching.loadTeacherGames({ status: CURRENT_GAME_STATUSES }))
        .catch(() => undefined);
    }
  }, [resumeWatchList, onWatchListResumed, role, teaching]);
  const [managedClassId, setManagedClassId] = useState('');
  const [playerOneMembershipId, setPlayerOneMembershipId] = useState('');
  const [playerTwoMembershipId, setPlayerTwoMembershipId] = useState('');
  const [guidedStudentMembershipId, setGuidedStudentMembershipId] = useState('');
  const [guidedStudentSearch, setGuidedStudentSearch] = useState('');
  const [createdClassRoomCode, setCreatedClassRoomCode] = useState<string | null>(null);
  const [assignedGamesView, setAssignedGamesView] = useState<AssignedGamesView>('CURRENT');
  const [reviewedAssignmentId, setReviewedAssignmentId] = useState<string | null>(null);

  useEffect(() => {
    if (role === 'ADMIN') void game.loadHistory();
    if (role === 'STUDENT') void homework.loadStudentLearning().catch(() => undefined);
  }, [game, homework, role]);

  useEffect(() => {
    if (resumeHomeworkReview) onHomeworkReviewResumed();
  }, [onHomeworkReviewResumed, resumeHomeworkReview]);

  useEffect(() => {
    if (dialog === null) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pending === null) setDialog(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [dialog, pending]);

  useEffect(() => {
    if (dialog !== 'MY_HOMEWORK') setReviewedAssignmentId(null);
  }, [dialog]);

  useEffect(() => {
    if (dialog !== 'HOMEWORK_ASSIGN' && dialog !== 'HOMEWORK_MENU') homework.dismissNotice();
  }, [dialog, homework]);

  const run = (label: string, operation: () => Promise<void>) => {
    setPending(label);
    void operation()
      .catch(() => undefined)
      .finally(() => setPending(null));
  };

  const openDialog = (next: LobbyDialog) => {
    if (role === 'STUDENT' && homework.snapshot().pending === 'open-assignment') return;
    game.clearError();
    teaching.clearError();
    homework.clearMessages();
    setDialog(next);
    if (next === 'TEACHING_MANAGE' || next === 'CREATE_MENU') {
      void teaching.loadTeacherDashboard().catch(() => undefined);
    }
    if (
      (next === 'CLASS_GAME_CREATE' || next === 'GUIDED_GAME_CREATE') &&
      teaching.snapshot().classes.length === 0
    ) {
      void teaching.loadTeacherDashboard().catch(() => undefined);
    }
    if (next === 'CLASS_GAME_VIEW') {
      void teaching
        .loadTeacherDashboard()
        .then(() => teaching.loadTeacherGames({ status: CURRENT_GAME_STATUSES }))
        .catch(() => undefined);
    }
    if (next === 'ASSIGNED_GAMES') {
      setAssignedGamesView('CURRENT');
      void teaching.loadAssignedGames().catch(() => undefined);
    }
    if (next === 'HISTORY') {
      void game.loadHistory();
    }
    if (next === 'HOMEWORK_ASSIGN') {
      void teaching.loadTeacherDashboard().catch(() => undefined);
    }
    if (next === 'AI_MANAGEMENT') {
      void admin.loadAiManagement().catch(() => undefined);
    }
    if (next === 'MY_HOMEWORK') {
      setReviewedAssignmentId(null);
      homework.clearSelection();
      void homework.loadMyAssignments().catch(() => undefined);
    }
  };

  const busy =
    pending !== null ||
    teachingState.pending !== null ||
    questionBankState.pending !== null ||
    homeworkState.pending !== null;
  const homeError =
    role === 'STUDENT' && dialog === null && homeworkState.error !== null
      ? [state.error, homeworkState.error].filter(Boolean).join('；')
      : state.error;
  const homeworkListDialog =
    dialog === 'HOMEWORK_REVIEW' || (dialog === 'MY_HOMEWORK' && reviewedAssignmentId === null);
  const analyticsDialog = dialog === 'HOMEWORK_DATA' || dialog === 'HOMEWORK_STUDENT_RESULTS';
  const teacherDataDialog = role === 'TEACHER' && (dialog === 'CLASS_GAME_VIEW' || analyticsDialog);
  const availableStudents = (classId: string) =>
    classId === ''
      ? teachingState.students
          .filter(
            (member) =>
              member.role === 'STUDENT' &&
              member.status === 'ACTIVE' &&
              member.user.status === 'ACTIVE',
          )
          .map((membership) => ({ membership, status: 'ACTIVE' as const }))
      : (teachingState.membersByClass[classId] ?? []);
  const classStudents = availableStudents(managedClassId);
  const guidedStudents = filterGuidedGameStudents(classStudents, guidedStudentSearch);
  const reviewedAssignment =
    reviewedAssignmentId === null
      ? null
      : homeworkState.selectedAssignment?.assignmentId === reviewedAssignmentId
        ? homeworkState.selectedAssignment
        : (homeworkState.assignments.find(
            (assignment) => assignment.assignmentId === reviewedAssignmentId,
          ) ?? null);
  const playerOneName =
    classStudents.find((member) => member.membership.membershipId === playerOneMembershipId)
      ?.membership.user.displayName ?? '棋手1';
  const dialogTitle: Readonly<Record<LobbyDialog, readonly [string, string]>> = {
    CREATE_MENU: ['CREATE GAME', '创建新对局'],
    CREATE: ['STANDARD GAME', '普通对局'],
    CURRENT_GAMES: ['MY GAMES', '我的对局列表'],
    ENTRY: ['GAME ENTRY', '加入/恢复对局'],
    JOIN: ['JOIN ROOM', '加入对局'],
    TEACHING_MANAGE: ['STUDENT INFORMATION', '学生信息'],
    CLASS_GAME_CREATE: ['STUDENT GAME', '学生对局'],
    GUIDED_GAME_CREATE: ['GUIDED GAME', '指导对弈'],
    CLASS_GAME_VIEW: ['STUDENT GAMES', '查看学生对局'],
    QUESTION_BANK: ['TEACHING LIBRARY', '题库管理'],
    ASSIGNED_GAMES: ['MY CLASS GAMES', '班级对弈'],
    HISTORY: ['MY GAME RECORDS', '我的棋谱与结果'],
    HOMEWORK_MENU: ['HOMEWORK', '作业管理'],
    HOMEWORK_ASSIGN: ['ASSIGN HOMEWORK', '布置作业'],
    HOMEWORK_REVIEW: ['REVIEW HOMEWORK', '批改作业'],
    HOMEWORK_DATA: ['HOMEWORK DATA', '作业数据'],
    HOMEWORK_STUDENT_RESULTS: ['STUDENT RESULTS', '学生成绩'],
    AI_MANAGEMENT: ['ZHIZI AI', 'AI管理'],
    MY_HOMEWORK: ['MY HOMEWORK', '我的作业'],
    WATCH_GAMES: ['WATCH GAMES', '观战对局列表'],
  };

  return (
    <main
      className={
        role === 'ADMIN'
          ? 'lobby-page'
          : `lobby-page role-home-lobby${role === 'STUDENT' ? ' student-home-lobby' : ' teacher-home-lobby'}`
      }
    >
      {role === 'ADMIN' ? (
        <>
          <section className="lobby-intro">
            <p className="eyebrow">对弈大厅</p>
            <h1>{displayName}，准备好下一盘了吗？</h1>
          </section>
          {state.error !== null ? (
            <p className="message message--error lobby-message">{state.error}</p>
          ) : null}
          <section className="lobby-actions" aria-label="对局功能">
            <button
              className="lobby-action-button lobby-action-button--accent"
              type="button"
              onClick={() => openDialog('CREATE')}
            >
              <span className="lobby-action-number">01</span>
              <small>CREATE ROOM</small>
              <strong>创建新对局</strong>
              <span className="lobby-action-arrow" aria-hidden="true">
                →
              </span>
            </button>
            <button
              className="lobby-action-button"
              type="button"
              onClick={() => openDialog('ENTRY')}
            >
              <span className="lobby-action-number">02</span>
              <small>JOIN · RESUME</small>
              <strong>加入/恢复对局</strong>
              <span className="lobby-action-arrow" aria-hidden="true">
                →
              </span>
            </button>
          </section>
        </>
      ) : (
        <RoleHome
          role={role}
          displayName={displayName}
          error={homeError}
          recovering={pending === 'recover'}
          onOpenDialog={openDialog}
          recoveringGameDisabled={busy}
          onOpenFoxKifu={onOpenFoxKifu}
          onOpenClassroomLecture={onOpenClassroomLecture}
          onOpenStudentPractice={onOpenStudentPractice}
          studentLearning={studentLearning}
          openingHomework={homeworkState.pending === 'open-assignment'}
          onOpenEbooks={onOpenEbooks}
          onLogout={onLogout}
          onContinueHomework={(assignmentId) => {
            if (homework.snapshot().pending !== null) return;
            onOpenHomework(assignmentId);
          }}
          onOpenHomeworkFeedback={() => openDialog('MY_HOMEWORK')}
          onRetryStudentLearning={() => {
            void homework.loadStudentLearning().catch(() => undefined);
          }}
        />
      )}
      {dialog !== null ? (
        <div
          className={`lobby-dialog-backdrop${roleDialogClassName(role)}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && pending === null) setDialog(null);
          }}
        >
          <section
            className={`lobby-dialog-card${dialog === 'CREATE' || dialog === 'CLASS_GAME_CREATE' || dialog === 'GUIDED_GAME_CREATE' ? ' lobby-dialog-card--accent' : ''}${dialog === 'CREATE_MENU' || dialog === 'TEACHING_MANAGE' || dialog === 'CLASS_GAME_CREATE' || dialog === 'GUIDED_GAME_CREATE' || dialog === 'CLASS_GAME_VIEW' || dialog === 'QUESTION_BANK' || dialog === 'HISTORY' || dialog === 'HOMEWORK_ASSIGN' || dialog === 'HOMEWORK_REVIEW' || dialog === 'HOMEWORK_DATA' || dialog === 'HOMEWORK_STUDENT_RESULTS' || dialog === 'AI_MANAGEMENT' || dialog === 'MY_HOMEWORK' ? ' lobby-dialog-card--wide' : ''}${dialog === 'TEACHING_MANAGE' ? ' lobby-dialog-card--student-info' : ''}${dialog === 'QUESTION_BANK' ? ' lobby-dialog-card--question-bank' : ''}${dialog === 'HOMEWORK_ASSIGN' || dialog === 'HOMEWORK_REVIEW' || dialog === 'HOMEWORK_DATA' || dialog === 'HOMEWORK_STUDENT_RESULTS' ? ' lobby-dialog-card--homework' : ''}${teacherDataDialog ? ' lobby-dialog-card--teacher-data' : ''}${homeworkListDialog ? ' lobby-dialog-card--homework-list' : ''}${dialog === 'MY_HOMEWORK' && reviewedAssignment !== null ? ' lobby-dialog-card--homework-result' : ''}${dialog === 'HISTORY' && role === 'STUDENT' ? ' lobby-dialog-card--student-history' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={dialogTitle[dialog][1]}
          >
            {dialog !== 'ASSIGNED_GAMES' ? (
              <RoleDialogHeading
                role={role}
                eyebrow={dialog !== 'QUESTION_BANK' ? dialogTitle[dialog][0] : undefined}
                title={dialogTitle[dialog][1]}
                detail={
                  dialog === 'HISTORY' ? (
                    <em className="history-dialog-count">
                      共 {state.history?.games.length ?? 0} 盘对局
                    </em>
                  ) : null
                }
                busy={busy}
                onClose={() => setDialog(null)}
              />
            ) : null}
            {dialog !== 'ASSIGNED_GAMES' && state.error !== null ? (
              <p className="message message--error">{state.error}</p>
            ) : null}
            {dialog !== 'ASSIGNED_GAMES' &&
            !analyticsDialog &&
            !homeworkListDialog &&
            teachingState.error !== null ? (
              <p className="message message--error">{teachingState.error}</p>
            ) : null}
            {(dialog === 'HOMEWORK_ASSIGN' ||
              dialog === 'HOMEWORK_MENU' ||
              dialog === 'MY_HOMEWORK') &&
            !analyticsDialog &&
            !homeworkListDialog &&
            homeworkState.error !== null ? (
              <p className="message message--error">{homeworkState.error}</p>
            ) : null}
            {(dialog === 'HOMEWORK_ASSIGN' || dialog === 'HOMEWORK_MENU') &&
            homeworkState.notice !== null ? (
              <div className="message message--success homework-publish-notice" role="status">
                <span>{homeworkState.notice}</span>
                <button
                  type="button"
                  aria-label="关闭作业提示"
                  onClick={() => homework.dismissNotice()}
                >
                  ×
                </button>
              </div>
            ) : null}
            {dialog === 'CURRENT_GAMES' ? <CurrentGamesPanel game={game} /> : null}
            {dialog === 'ENTRY' ? (
              <div className="create-game-menu entry-choice-menu">
                <div className="dialog-choice-grid">
                  <button type="button" disabled={busy} onClick={() => openDialog('CURRENT_GAMES')}>
                    <strong>对局列表</strong>
                    <small>选择等待准备或进行中的对局，进入或恢复</small>
                  </button>
                  <button type="button" disabled={busy} onClick={() => openDialog('JOIN')}>
                    <strong>加入对局</strong>
                    <small>输入房间号加入好友创建的对局</small>
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setDialog(null);
                      run('recover', () => game.recoverLatestGame());
                    }}
                  >
                    <strong>恢复最近对局</strong>
                    <small>无需房间号，直接恢复最近一盘未结束对局</small>
                  </button>
                </div>
              </div>
            ) : null}
            {dialog === 'HOMEWORK_MENU' ? (
              <div className="create-game-menu homework-menu">
                <div className="dialog-choice-grid">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openDialog('HOMEWORK_ASSIGN')}
                  >
                    <strong>布置作业</strong>
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openDialog('HOMEWORK_REVIEW')}
                  >
                    <strong>批改作业</strong>
                  </button>
                  <button type="button" disabled={busy} onClick={() => openDialog('HOMEWORK_DATA')}>
                    <strong>作业数据</strong>
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openDialog('HOMEWORK_STUDENT_RESULTS')}
                  >
                    <strong>学生成绩</strong>
                  </button>
                </div>
              </div>
            ) : null}
            {dialog === 'HOMEWORK_ASSIGN' ? (
              <HomeworkAssignmentComposer
                homework={homework}
                teaching={teaching}
                questionBank={questionBank}
                onCancel={() => openDialog('HOMEWORK_MENU')}
                onCreated={() => setDialog('HOMEWORK_MENU')}
              />
            ) : null}
            {dialog === 'HOMEWORK_REVIEW' ? (
              <HomeworkReviewListPanel
                homework={homework}
                teaching={teaching}
                onCancel={() => openDialog('HOMEWORK_MENU')}
                onOpen={(homeworkId) => {
                  setDialog(null);
                  onOpenHomeworkReview(homeworkId);
                }}
              />
            ) : null}
            {dialog === 'HOMEWORK_DATA' || dialog === 'HOMEWORK_STUDENT_RESULTS' ? (
              <HomeworkAnalyticsPanel
                mode={dialog}
                homework={homework}
                teaching={teaching}
                onCancel={() => openDialog('HOMEWORK_MENU')}
              />
            ) : null}
            {dialog === 'WATCH_GAMES' &&
            role === 'STUDENT' &&
            activity !== undefined &&
            onWatchGame !== undefined ? (
              <StudentWatchGamesPanel api={activity} onWatch={onWatchGame} />
            ) : null}
            {dialog === 'MY_HOMEWORK' ? (
              reviewedAssignment === null ? (
                <StudentHomeworkListPanel
                  assignments={homeworkState.assignments}
                  loading={homeworkState.pending === 'load-my-homework'}
                  busy={busy}
                  error={homeworkState.error}
                  onRefresh={() => {
                    void homework.loadMyAssignments().catch(() => undefined);
                  }}
                  onOpen={(assignment) => {
                    if (studentHomeworkOpenAction(assignment.status) === 'OPEN_ANSWER') {
                      onOpenHomework(assignment.assignmentId);
                      return;
                    }
                    setReviewedAssignmentId(assignment.assignmentId);
                    void homework.selectAssignment(assignment.assignmentId).catch(() => undefined);
                  }}
                />
              ) : (
                <StudentHomeworkResultPanel
                  key={reviewedAssignment.assignmentId}
                  homework={homework}
                  assignment={reviewedAssignment}
                  refreshing={homeworkState.pending === 'open-assignment'}
                  onBack={() => {
                    setReviewedAssignmentId(null);
                    homework.clearSelection();
                  }}
                />
              )
            ) : null}
            {dialog === 'CREATE_MENU' ? (
              <div className="create-game-menu">
                <p>选择本次对局类型，后续仍由服务端统一创建棋局、裁判并记录结果。</p>
                <div className="dialog-choice-grid dialog-choice-grid--three">
                  <button type="button" disabled={busy} onClick={() => openDialog('CREATE')}>
                    <strong>普通对局</strong>
                    <small>创建房间并邀请对手加入</small>
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setCreatedClassRoomCode(null);
                      openDialog('CLASS_GAME_CREATE');
                    }}
                  >
                    <strong>学生对局</strong>
                    <small>为两名学生安排对局，可跨班选择</small>
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setGuidedStudentSearch('');
                      openDialog('GUIDED_GAME_CREATE');
                    }}
                  >
                    <strong>指导对弈</strong>
                    <small>教师执白，与所选学生对弈</small>
                  </button>
                </div>
              </div>
            ) : null}
            {dialog === 'CREATE' ? (
              <>
                {role === 'STUDENT' ? (
                  <p className="game-creation-guide">
                    9 路适合练习吃子与围地，13 路用于过渡，19
                    路练习完整对局。老师有安排时，请使用老师要求的设置。
                  </p>
                ) : null}
                <label>
                  棋盘路数
                  <span className="segmented-control">
                    {([9, 13, 19] as const).map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={boardSize === size ? 'is-selected' : ''}
                        onClick={() => setBoardSize(size)}
                      >
                        {size} 路
                      </button>
                    ))}
                  </span>
                </label>
                <label>
                  每方基本时间
                  <select
                    value={mainTimeMs}
                    onChange={(event) => setMainTimeMs(Number(event.target.value))}
                  >
                    {MAIN_TIME_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="form-grid form-grid--two">
                  <label>
                    读秒
                    <select
                      value={byoyomiPeriodTimeMs ?? 0}
                      onChange={(event) =>
                        setByoyomiPeriodTimeMs(
                          Number(event.target.value) === 0
                            ? null
                            : (Number(event.target.value) as 10_000 | 15_000 | 30_000 | 60_000),
                        )
                      }
                    >
                      <option value={0}>不设置</option>
                      <option value={10_000}>10s</option>
                      <option value={15_000}>15s</option>
                      <option value={30_000}>30s</option>
                      <option value={60_000}>60s</option>
                    </select>
                  </label>
                  <label>
                    读秒次数
                    <select
                      value={byoyomiPeriods}
                      disabled={byoyomiPeriodTimeMs === null}
                      onChange={(event) =>
                        setByoyomiPeriods(Number(event.target.value) as 1 | 2 | 3 | 5 | 10)
                      }
                    >
                      {[1, 2, 3, 5, 10].map((periods) => (
                        <option key={periods} value={periods}>
                          {periods}次
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    棋份
                    <select
                      value={gameType}
                      onChange={(event) => setGameType(event.target.value as GameType)}
                    >
                      <option value="EVEN">分先</option>
                      <option value="LET_FIRST">让先</option>
                      <option value="HANDICAP_2">让2子</option>
                      <option value="HANDICAP_3">让3子</option>
                      <option value="HANDICAP_4">让4子</option>
                    </select>
                  </label>
                  <label>
                    执棋
                    <select
                      value={creatorColor}
                      onChange={(event) =>
                        setCreatorColor(event.target.value as CreatorColorPreference)
                      }
                    >
                      <option value="BLACK">执黑</option>
                      <option value="WHITE">执白</option>
                      <option value="RANDOM">随机</option>
                    </select>
                  </label>
                </div>
                {role === 'STUDENT' ? (
                  <p className="game-creation-guide">
                    读秒是在基本时间用完后使用的每手限时；不设置读秒时，基本时间用完即超时。棋份请按双方棋力或老师安排选择。
                  </p>
                ) : null}
                <button
                  className="primary-button primary-button--wide"
                  type="button"
                  disabled={pending !== null}
                  onClick={() =>
                    run('create', () =>
                      game.createRoom({
                        boardSize,
                        mainTimeMs,
                        byoyomiPeriodTimeMs,
                        byoyomiPeriods,
                        gameType,
                        creatorColor,
                      }),
                    )
                  }
                >
                  {pending === 'create' ? '正在创建…' : '创建房间'}
                </button>
              </>
            ) : null}
            {dialog === 'JOIN' ? (
              <>
                <p>输入对方分享的 6–12 位房间码。</p>
                <input
                  className="room-code-input"
                  value={roomCode}
                  onChange={(event) => setRoomCode(event.target.value.toUpperCase())}
                  placeholder="例如 A7CD9K"
                  maxLength={12}
                  autoFocus
                />
                <button
                  className="secondary-button secondary-button--wide"
                  type="button"
                  disabled={pending !== null || roomCode.trim().length < 6}
                  onClick={() => run('join', () => game.joinRoom(roomCode))}
                >
                  {pending === 'join' ? '正在加入…' : '使用房间码加入'}
                </button>
              </>
            ) : null}
            {dialog === 'CLASS_GAME_CREATE' ? (
              <div className="class-game-create-form">
                <div className="form-grid form-grid--two">
                  <label>
                    班级（选填）
                    <select
                      value={managedClassId}
                      onChange={(event) => {
                        const classId = event.target.value;
                        const members = availableStudents(classId);
                        setManagedClassId(classId);
                        setPlayerOneMembershipId(members[0]?.membership.membershipId ?? '');
                        setPlayerTwoMembershipId(members[1]?.membership.membershipId ?? '');
                        setCreatedClassRoomCode(null);
                      }}
                    >
                      <option value="">不限定班级</option>
                      {teachingState.classes.map((managedClassOption) => (
                        <option key={managedClassOption.classId} value={managedClassOption.classId}>
                          {managedClassOption.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    棋盘路数
                    <select
                      value={boardSize}
                      onChange={(event) => setBoardSize(Number(event.target.value) as 9 | 13 | 19)}
                    >
                      <option value={9}>9 路</option>
                      <option value={13}>13 路</option>
                      <option value={19}>19 路</option>
                    </select>
                  </label>
                  <label>
                    棋手1
                    <select
                      value={playerOneMembershipId}
                      onChange={(event) => setPlayerOneMembershipId(event.target.value)}
                    >
                      <option value="">请选择学生</option>
                      {classStudents.map((member) => (
                        <option
                          key={member.membership.membershipId}
                          value={member.membership.membershipId}
                          disabled={member.membership.membershipId === playerTwoMembershipId}
                        >
                          {member.membership.user.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    棋手2
                    <select
                      value={playerTwoMembershipId}
                      onChange={(event) => setPlayerTwoMembershipId(event.target.value)}
                    >
                      <option value="">请选择学生</option>
                      {classStudents.map((member) => (
                        <option
                          key={member.membership.membershipId}
                          value={member.membership.membershipId}
                          disabled={member.membership.membershipId === playerOneMembershipId}
                        >
                          {member.membership.user.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    每方基本时间
                    <select
                      value={mainTimeMs}
                      onChange={(event) => setMainTimeMs(Number(event.target.value))}
                    >
                      {MAIN_TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    读秒
                    <select
                      value={byoyomiPeriodTimeMs ?? 0}
                      onChange={(event) =>
                        setByoyomiPeriodTimeMs(
                          Number(event.target.value) === 0
                            ? null
                            : (Number(event.target.value) as 10_000 | 15_000 | 30_000 | 60_000),
                        )
                      }
                    >
                      <option value={0}>不设置</option>
                      <option value={10_000}>10s</option>
                      <option value={15_000}>15s</option>
                      <option value={30_000}>30s</option>
                      <option value={60_000}>60s</option>
                    </select>
                  </label>
                  <label>
                    读秒次数
                    <select
                      value={byoyomiPeriods}
                      disabled={byoyomiPeriodTimeMs === null}
                      onChange={(event) =>
                        setByoyomiPeriods(Number(event.target.value) as 1 | 2 | 3 | 5 | 10)
                      }
                    >
                      {[1, 2, 3, 5, 10].map((periods) => (
                        <option key={periods} value={periods}>
                          {periods}次
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    棋份
                    <select
                      value={gameType}
                      onChange={(event) => setGameType(event.target.value as GameType)}
                    >
                      <option value="EVEN">分先</option>
                      <option value="LET_FIRST">让先</option>
                      <option value="HANDICAP_2">让2子</option>
                      <option value="HANDICAP_3">让3子</option>
                      <option value="HANDICAP_4">让4子</option>
                    </select>
                  </label>
                  <label>
                    执棋
                    <select
                      value={creatorColor}
                      onChange={(event) =>
                        setCreatorColor(event.target.value as CreatorColorPreference)
                      }
                    >
                      <option value="RANDOM">随机（进入猜先）</option>
                      <option value="BLACK">{playerOneName}执黑</option>
                      <option value="WHITE">{playerOneName}执白</option>
                    </select>
                  </label>
                </div>
                {createdClassRoomCode !== null ? (
                  <p className="message message--success">
                    对局已创建，房间码 {createdClassRoomCode}。两名学生可从“我的对局列表”进入。
                  </p>
                ) : null}
                <button
                  className="primary-button primary-button--wide"
                  type="button"
                  disabled={
                    busy ||
                    playerOneMembershipId === '' ||
                    playerTwoMembershipId === '' ||
                    playerOneMembershipId === playerTwoMembershipId
                  }
                  onClick={() =>
                    run('class-game-create', async () => {
                      const snapshot = await teaching.createClassGame({
                        classId: managedClassId,
                        playerOneMembershipId,
                        playerTwoMembershipId,
                        boardSize,
                        mainTimeMs,
                        byoyomiPeriodTimeMs,
                        byoyomiPeriods,
                        gameType,
                        playerOneColor: creatorColor,
                      });
                      setCreatedClassRoomCode(snapshot.room.roomCode);
                    })
                  }
                >
                  {busy ? '正在创建…' : '创建学生对局'}
                </button>
              </div>
            ) : null}
            {dialog === 'GUIDED_GAME_CREATE' ? (
              <div className="guided-game-create-form">
                <div className="guided-game-notice">
                  <strong>教师固定执白</strong>
                  <span>所选学生执黑，可从“我的对局列表”或通过房间码进入。</span>
                </div>
                <div className="form-grid form-grid--two">
                  <label>
                    班级（选填）
                    <select
                      value={managedClassId}
                      onChange={(event) => {
                        const classId = event.target.value;
                        const members = availableStudents(classId);
                        setManagedClassId(classId);
                        setGuidedStudentSearch('');
                        setGuidedStudentMembershipId(
                          filterGuidedGameStudents(members, '')[0]?.membership.membershipId ?? '',
                        );
                      }}
                    >
                      <option value="">不限定班级</option>
                      {teachingState.classes.map((managedClassOption) => (
                        <option key={managedClassOption.classId} value={managedClassOption.classId}>
                          {managedClassOption.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    快速查找学生
                    <input
                      value={guidedStudentSearch}
                      placeholder="输入姓名首字或前两个字"
                      onChange={(event) => {
                        const keyword = event.target.value;
                        const matches = filterGuidedGameStudents(classStudents, keyword);
                        setGuidedStudentSearch(keyword);
                        setGuidedStudentMembershipId((current) =>
                          matches.some((student) => student.membership.membershipId === current)
                            ? current
                            : (matches[0]?.membership.membershipId ?? ''),
                        );
                      }}
                    />
                  </label>
                  <label>
                    学生（执黑）
                    <select
                      value={guidedStudentMembershipId}
                      disabled={guidedStudents.length === 0}
                      onChange={(event) => setGuidedStudentMembershipId(event.target.value)}
                    >
                      <option value="">
                        {guidedStudents.length === 0 ? '未找到匹配学生' : '请选择学生'}
                      </option>
                      {guidedStudents.map((member) => (
                        <option
                          key={member.membership.membershipId}
                          value={member.membership.membershipId}
                        >
                          {member.membership.user.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    教师执棋
                    <input value="白棋（固定）" disabled readOnly />
                  </label>
                  <label>
                    棋盘路数
                    <select
                      value={boardSize}
                      onChange={(event) => setBoardSize(Number(event.target.value) as 9 | 13 | 19)}
                    >
                      <option value={9}>9 路</option>
                      <option value={13}>13 路</option>
                      <option value={19}>19 路</option>
                    </select>
                  </label>
                  <label>
                    每方基本时间
                    <select
                      value={mainTimeMs}
                      onChange={(event) => setMainTimeMs(Number(event.target.value))}
                    >
                      {MAIN_TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    读秒
                    <select
                      value={byoyomiPeriodTimeMs ?? 0}
                      onChange={(event) =>
                        setByoyomiPeriodTimeMs(
                          Number(event.target.value) === 0
                            ? null
                            : (Number(event.target.value) as 10_000 | 15_000 | 30_000 | 60_000),
                        )
                      }
                    >
                      <option value={0}>不设置</option>
                      <option value={10_000}>10s</option>
                      <option value={15_000}>15s</option>
                      <option value={30_000}>30s</option>
                      <option value={60_000}>60s</option>
                    </select>
                  </label>
                  <label>
                    读秒次数
                    <select
                      value={byoyomiPeriods}
                      disabled={byoyomiPeriodTimeMs === null}
                      onChange={(event) =>
                        setByoyomiPeriods(Number(event.target.value) as 1 | 2 | 3 | 5 | 10)
                      }
                    >
                      {[1, 2, 3, 5, 10].map((periods) => (
                        <option key={periods} value={periods}>
                          {periods}次
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    棋份
                    <select
                      value={gameType}
                      onChange={(event) => setGameType(event.target.value as GameType)}
                    >
                      <option value="EVEN">分先</option>
                      <option value="LET_FIRST">让先</option>
                      <option value="HANDICAP_2">让2子</option>
                      <option value="HANDICAP_3">让3子</option>
                      <option value="HANDICAP_4">让4子</option>
                    </select>
                  </label>
                </div>
                <button
                  className="primary-button primary-button--wide"
                  type="button"
                  disabled={busy || guidedStudentMembershipId === ''}
                  onClick={() =>
                    run('guided-game-create', async () => {
                      const snapshot = await teaching.createGuidedGame({
                        classId: managedClassId,
                        studentMembershipId: guidedStudentMembershipId,
                        boardSize,
                        mainTimeMs,
                        byoyomiPeriodTimeMs,
                        byoyomiPeriods,
                        gameType,
                      });
                      await game.loadGame(snapshot.gameId);
                      setDialog(null);
                    })
                  }
                >
                  {pending === 'guided-game-create' ? '正在创建…' : '创建指导对弈'}
                </button>
              </div>
            ) : null}
            {dialog === 'CLASS_GAME_VIEW' ? (
              <TeacherGamesView
                game={game}
                teaching={teaching}
                state={teachingState}
                onOpenReview={onOpenHistoryReview}
                onWatchGame={onWatchGame}
              />
            ) : null}
            {dialog === 'TEACHING_MANAGE' ? (
              <StudentInformationPanel
                teaching={teaching}
                state={teachingState}
                onOpenReview={(gameId, student) =>
                  onOpenHistoryReview(gameId, {
                    scope: 'STUDENT',
                    filters: { studentMembershipId: student.membershipId },
                    title: `${student.user.displayName}（${student.accountUsername ?? '微信账号'}）的棋谱`,
                  })
                }
              />
            ) : null}
            {dialog === 'QUESTION_BANK' ? <QuestionBankPanel store={questionBank} /> : null}
            {dialog === 'AI_MANAGEMENT' ? <TeacherAiManagementPanel admin={admin} /> : null}
            {dialog === 'ASSIGNED_GAMES' ? (
              <AssignedClassGamesPanel
                games={teachingState.assignedGames}
                view={assignedGamesView}
                busy={busy}
                loading={teachingState.pending === 'load-assigned-games'}
                error={teachingState.error ?? state.error}
                onRefresh={() => {
                  void teaching.loadAssignedGames().catch(() => undefined);
                }}
                onViewChange={(view) => {
                  setAssignedGamesView(view);
                  void teaching.loadAssignedGames().catch(() => undefined);
                }}
                onClose={() => setDialog(null)}
                onOpen={(id) => run('open-assigned', () => game.loadGame(id))}
              />
            ) : null}
            {dialog === 'HISTORY' ? (
              role === 'STUDENT' ? (
                <StudentGameHistoryPanel
                  games={state.history?.games ?? []}
                  loading={state.historyPending}
                  downloadPendingGameId={state.downloadPendingGameId}
                  onView={onOpenHistoryReview}
                  onDownload={(gameId) => void game.downloadSgf(gameId)}
                />
              ) : (
                <HistoryDialog game={game} state={state} onOpenReview={onOpenHistoryReview} />
              )
            ) : null}
          </section>
        </div>
      ) : null}
      {role === 'ADMIN' ? <HistoryPanel game={game} state={state} /> : null}
      {role === 'ADMIN' ? (
        <section className="authority-note">
          <strong>服务端权威</strong>
          <span>棋盘、计时、规则、悔棋和终局结果均由服务器确认，刷新网页不会丢失对局数据。</span>
        </section>
      ) : null}
    </main>
  );
}

export function TeacherGamesView({
  game,
  teaching,
  state,
  onOpenReview,
  onWatchGame,
  initialView = 'CURRENT',
}: {
  readonly game: GameStore;
  readonly teaching: TeachingStore;
  readonly state: ReturnType<TeachingStore['snapshot']>;
  readonly onOpenReview: (gameId: string, context?: TeacherGameListContext) => void;
  readonly onWatchGame?: ((gameId: string) => void) | undefined;
  readonly initialView?: 'CURRENT' | 'HISTORY' | 'ALL';
}) {
  const gameState = useSyncExternalStore(game.subscribe, game.snapshot, game.snapshot);
  const [classId, setClassId] = useState('');
  const [studentMembershipId, setStudentMembershipId] = useState('');
  const [gameView, setGameView] = useState<'CURRENT' | 'HISTORY' | 'ALL'>(initialView);
  const visibleGames = state.teacherGames.filter(
    (candidate) =>
      gameView === 'ALL' ||
      (gameView === 'CURRENT'
        ? candidate.status !== 'FINISHED' && candidate.status !== 'ABORTED'
        : candidate.status === 'FINISHED' || candidate.status === 'ABORTED'),
  );
  const [correctionGameId, setCorrectionGameId] = useState<string | null>(null);
  const [correctionWinner, setCorrectionWinner] = useState<StoneColor>('BLACK');
  const [correctionReason, setCorrectionReason] = useState('');
  const selectedClassMembershipIds = new Set(
    classId === ''
      ? []
      : (state.membersByClass[classId] ?? []).map((member) => member.membership.membershipId),
  );
  const availableStudents = state.students.filter(
    (student) =>
      student.status === 'ACTIVE' &&
      (classId === '' || selectedClassMembershipIds.has(student.membershipId)),
  );
  const reviewListTitle =
    state.teacherGameFilters.classId === undefined
      ? '当前负责班级学生棋谱'
      : `${
          state.classes.find(
            (managedClass) => managedClass.classId === state.teacherGameFilters.classId,
          )?.name ?? '当前班级'
        }学生棋谱`;
  const loadFilteredGames = (
    nextClassId: string,
    nextStudentMembershipId: string,
    nextView = gameView,
  ) =>
    teaching.loadTeacherGames({
      ...(nextView === 'ALL'
        ? {}
        : {
            status:
              nextView === 'CURRENT' ? CURRENT_GAME_STATUSES : (['FINISHED', 'ABORTED'] as const),
          }),
      ...(nextClassId === '' ? {} : { classId: nextClassId }),
      ...(nextStudentMembershipId === '' ? {} : { studentMembershipId: nextStudentMembershipId }),
    });
  const correctionGame =
    correctionGameId === null
      ? null
      : (state.teacherGames.find((candidate) => candidate.gameId === correctionGameId) ?? null);

  return (
    <div className="teacher-data teacher-data--games">
      <div className="teacher-data__intro">
        <p>进行中的对局点击“观战”；已结束对局切换到“历史对局”查看棋谱。</p>
        <span className="teacher-data__count">
          {state.pending !== null ? '正在加载…' : `当前列表 ${visibleGames.length} 盘`}
        </span>
      </div>
      <div className="teacher-data__view-tabs" role="group" aria-label="学生对局状态筛选">
        {(
          [
            ['CURRENT', '当前对局'],
            ['HISTORY', '历史对局'],
            ['ALL', '全部'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={gameView === value}
            disabled={state.pending !== null}
            onClick={() => {
              setGameView(value);
              void loadFilteredGames(classId, studentMembershipId, value).catch(() => undefined);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="teacher-data__filters" aria-label="学生对局筛选">
        <label>
          班级
          <select
            aria-label="班级"
            value={classId}
            disabled={state.pending !== null}
            onChange={(event) => {
              const nextClassId = event.target.value;
              setClassId(nextClassId);
              setStudentMembershipId('');
              void loadFilteredGames(nextClassId, '').catch(() => undefined);
            }}
          >
            <option value="">全部班级</option>
            {state.classes.map((managedClass) => (
              <option key={managedClass.classId} value={managedClass.classId}>
                {managedClass.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          学生
          <select
            aria-label="学生"
            value={studentMembershipId}
            disabled={state.pending !== null}
            onChange={(event) => {
              const nextStudentMembershipId = event.target.value;
              setStudentMembershipId(nextStudentMembershipId);
              void loadFilteredGames(classId, nextStudentMembershipId).catch(() => undefined);
            }}
          >
            <option value="">全部学生</option>
            {availableStudents.map((student) => (
              <option key={student.membershipId} value={student.membershipId}>
                {student.user.displayName}（{student.accountUsername ?? '微信账号'}）
              </option>
            ))}
          </select>
        </label>
        <button
          className="teacher-data__button teacher-data__button--primary"
          type="button"
          disabled={state.pending !== null}
          onClick={() =>
            void loadFilteredGames(classId, studentMembershipId).catch(() => undefined)
          }
        >
          {state.pending !== null ? '刷新中…' : '刷新'}
        </button>
      </div>
      {state.pending === 'load-teacher' || state.pending === 'load-teacher-games' ? (
        <p className="teacher-data__notice" role="status">
          正在加载学生对局…
        </p>
      ) : null}
      {visibleGames.length === 0 && state.pending === null ? (
        <div className="teacher-data__empty" role="status">
          <strong>暂无学生对局</strong>
          <span>当前筛选条件下没有对局，可切换班级或学生后查看。</span>
        </div>
      ) : null}
      {visibleGames.length > 0 ? (
        <div
          className="teacher-data__table-wrap"
          aria-busy={state.pending === 'load-teacher-games'}
        >
          <table
            className="teacher-data__table teacher-data__table--games"
            aria-label="学生对局列表"
          >
            <thead>
              <tr>
                <th scope="col">对局双方</th>
                <th scope="col">日期 / 棋盘</th>
                <th scope="col">状态 / 进度</th>
                <th scope="col">操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleGames.map((teacherGame) => {
                const black = teacherGame.players.find((player) => player.seat === 'BLACK');
                const white = teacherGame.players.find((player) => player.seat === 'WHITE');
                const terminal = teacherGame.status === 'FINISHED';
                const canDelete = ['WAITING', 'READY', 'FINISHED', 'ABORTED'].includes(
                  teacherGame.status,
                );
                const canManageDeletion =
                  teacherGame.accessLevel === 'MANAGE' ||
                  (teacherGame.accessLevel === 'OBSERVE' &&
                    ['WAITING', 'READY', 'ABORTED'].includes(teacherGame.status) &&
                    teacherGame.players.length === 1);
                return (
                  <tr key={teacherGame.gameId}>
                    <th scope="row" className="teacher-data__players">
                      <span>
                        <i className="teacher-data__stone" aria-hidden="true" />
                        <small>黑方</small>
                        <strong>{black?.displayName ?? '—'}</strong>
                      </span>
                      <span>
                        <i
                          className="teacher-data__stone teacher-data__stone--white"
                          aria-hidden="true"
                        />
                        <small>白方</small>
                        <strong>{white?.displayName ?? '—'}</strong>
                      </span>
                    </th>
                    <td data-label="日期 / 棋盘">
                      <span className="teacher-data__cell-stack">
                        <span>
                          {new Date(
                            teacherGame.startedAt ?? teacherGame.updatedAt,
                          ).toLocaleDateString('zh-CN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </span>
                        <small>{teacherGame.boardSize} 路</small>
                      </span>
                    </td>
                    <td data-label="状态 / 进度">
                      <span className="teacher-data__cell-stack">
                        <span className="teacher-data__status" data-status={teacherGame.status}>
                          {STATUS_LABELS[teacherGame.status]}
                        </span>
                        <small>{teacherGame.currentSequence} 手</small>
                      </span>
                    </td>
                    <td className="teacher-data__actions-cell">
                      <div className="teacher-data__actions">
                        {!terminal &&
                        teacherGame.status !== 'ABORTED' &&
                        onWatchGame !== undefined ? (
                          <button
                            className="teacher-data__button teacher-data__button--primary"
                            type="button"
                            onClick={() => onWatchGame(teacherGame.gameId)}
                          >
                            观战
                          </button>
                        ) : null}
                        <button
                          className="teacher-data__button"
                          type="button"
                          title={terminal ? undefined : '对局结束后可下载 SGF 棋谱'}
                          disabled={!terminal || gameState.downloadPendingGameId !== null}
                          onClick={() => void game.downloadSgf(teacherGame.gameId)}
                        >
                          {gameState.downloadPendingGameId === teacherGame.gameId
                            ? '下载中…'
                            : '下载'}
                        </button>
                        <button
                          className="teacher-data__button teacher-data__button--primary"
                          type="button"
                          title={terminal ? undefined : '对局结束后可查看棋谱'}
                          disabled={!terminal}
                          onClick={() =>
                            onOpenReview(teacherGame.gameId, {
                              scope: 'CLASS',
                              filters: state.teacherGameFilters,
                              title: reviewListTitle,
                            })
                          }
                        >
                          查看
                        </button>
                        <button
                          className="teacher-data__button teacher-data__button--caution"
                          type="button"
                          title={terminal ? undefined : '仅已结束的对局可修改结果'}
                          disabled={!terminal || state.pending !== null}
                          onClick={() => {
                            setCorrectionGameId(teacherGame.gameId);
                            setCorrectionWinner('BLACK');
                            setCorrectionReason('');
                          }}
                        >
                          修改
                        </button>
                        <button
                          className="teacher-data__button teacher-data__button--danger"
                          type="button"
                          disabled={state.pending !== null || !canDelete || !canManageDeletion}
                          title={
                            !canManageDeletion
                              ? '需要该对局的班级管理权限'
                              : canDelete
                                ? '删除此对局'
                                : '进行中的对局不能删除，请在对局结束后再删除'
                          }
                          aria-label={`删除${black?.displayName ?? '黑方'}与${white?.displayName ?? '白方'}的对局`}
                          onClick={() => {
                            const waiting =
                              teacherGame.status === 'WAITING' || teacherGame.status === 'READY';
                            if (
                              window.confirm(
                                `确认删除 ${black?.displayName ?? '黑方'} 对 ${white?.displayName ?? '白方'} 的对局？\n${waiting ? '尚未开始的对局将被取消，双方不能再加入或开始。' : '对局将从师生对局与棋谱列表移除。'}\n原始记录和审计信息保留。`,
                              )
                            ) {
                              void teaching.deleteGame(teacherGame).catch(() => undefined);
                            }
                          }}
                        >
                          {state.pending === 'delete-game-record' ? '删除中…' : '删除'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className="teacher-data__footnote">
        仅展示当前负责班级的对局，权限由服务端逐局校验。等待准备和已结束的对局可删除；进行中的对局请结束后再删除。
      </p>
      {correctionGame !== null ? (
        <div className="management-subdialog-backdrop" role="presentation">
          <form
            className="management-subdialog-card"
            aria-label="修改学生对局结果"
            onSubmit={(event) => {
              event.preventDefault();
              if (correctionReason.trim().length === 0) return;
              void teaching
                .correctTeacherGameResult(
                  correctionGame.gameId,
                  correctionGame.stateVersion,
                  correctionWinner,
                  correctionReason,
                )
                .then(() => loadFilteredGames(classId, studentMembershipId))
                .then(() => {
                  setCorrectionGameId(null);
                  setCorrectionReason('');
                })
                .catch(() => undefined);
            }}
          >
            <div className="lobby-dialog-heading">
              <div>
                <p className="eyebrow">RESULT CORRECTION</p>
                <h3>修改学生对局结果</h3>
              </div>
            </div>
            <p className="form-hint">
              修改会保留原结果和教师操作审计记录，新结果按教师判定胜负保存。
            </p>
            <label>
              判定胜方
              <select
                value={correctionWinner}
                disabled={state.pending !== null}
                onChange={(event) => setCorrectionWinner(event.target.value as StoneColor)}
              >
                <option value="BLACK">
                  黑方 ·{' '}
                  {correctionGame.players.find((player) => player.seat === 'BLACK')?.displayName ??
                    '—'}
                </option>
                <option value="WHITE">
                  白方 ·{' '}
                  {correctionGame.players.find((player) => player.seat === 'WHITE')?.displayName ??
                    '—'}
                </option>
              </select>
            </label>
            <label>
              修改原因
              <textarea
                value={correctionReason}
                maxLength={500}
                required
                disabled={state.pending !== null}
                placeholder="请填写修改依据，系统将写入审计记录"
                onChange={(event) => setCorrectionReason(event.target.value)}
              />
            </label>
            <div className="dialog-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={state.pending !== null}
                onClick={() => setCorrectionGameId(null)}
              >
                取消
              </button>
              <button
                className="primary-button"
                type="submit"
                disabled={state.pending !== null || correctionReason.trim().length === 0}
              >
                {state.pending === `teacher-result-correction-${correctionGame.gameId}`
                  ? '正在修改…'
                  : '确认修改'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function HistoryPanel({
  game,
  state,
}: {
  readonly game: GameStore;
  readonly state: ReturnType<GameStore['snapshot']>;
}) {
  const history = state.history?.games ?? [];
  const [expanded, setExpanded] = useState(false);
  return (
    <section className={expanded ? 'history-panel is-expanded' : 'history-panel is-collapsed'}>
      <div className="history-heading">
        <div>
          <p className="eyebrow">MY GAME RECORDS</p>
          <h2>我的棋谱与结果</h2>
          <p>
            {state.historyPending
              ? '正在读取历史记录…'
              : `已保存 ${history.length} 盘对局，可随时恢复查看或下载 SGF。`}
          </p>
        </div>
        <div className="history-heading-actions">
          {expanded ? (
            <button
              className="secondary-button"
              type="button"
              disabled={state.historyPending}
              onClick={() => void game.loadHistory()}
            >
              {state.historyPending ? '正在刷新…' : '刷新记录'}
            </button>
          ) : null}
          <button
            className="history-toggle"
            type="button"
            aria-expanded={expanded}
            aria-controls="player-game-history"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? '收起' : '展开'}
            <span aria-hidden="true">{expanded ? '↑' : '↓'}</span>
          </button>
        </div>
      </div>
      {expanded ? (
        <div id="player-game-history">
          <HistoryList
            game={game}
            state={state}
            games={history}
            onView={(gameId) => void game.loadGame(gameId)}
          />
        </div>
      ) : null}
    </section>
  );
}

function HistoryDialog({
  game,
  state,
  onOpenReview,
}: {
  readonly game: GameStore;
  readonly state: ReturnType<GameStore['snapshot']>;
  readonly onOpenReview: (gameId: string) => void;
}) {
  const history = state.history?.games ?? [];
  const [dateFilter, setDateFilter] = useState('');
  const filteredHistory = useMemo(
    () => filterHistoryByDate(history, dateFilter),
    [dateFilter, history],
  );
  return (
    <div className="history-dialog-content">
      <div className="history-dialog-filters" aria-label="按对局日期筛选我的棋谱">
        <label>
          对局日期
          <input
            type="date"
            value={dateFilter}
            disabled={state.historyPending}
            onChange={(event) => setDateFilter(event.target.value)}
          />
        </label>
        <button
          className="secondary-button"
          type="button"
          disabled={dateFilter === '' || state.historyPending}
          onClick={() => setDateFilter('')}
        >
          显示全部日期
        </button>
        <span>
          {state.historyPending
            ? '正在读取…'
            : `显示 ${filteredHistory.length} / ${history.length} 盘`}
        </span>
      </div>
      <HistoryList
        game={game}
        state={state}
        games={filteredHistory}
        {...(dateFilter === '' ? {} : { emptyMessage: '所选日期没有对局记录。' })}
        onView={onOpenReview}
      />
    </div>
  );
}

function HistoryList({
  game,
  state,
  games,
  emptyMessage,
  onView,
}: {
  readonly game: GameStore;
  readonly state: ReturnType<GameStore['snapshot']>;
  readonly games: readonly GameHistoryItem[];
  readonly emptyMessage?: string;
  readonly onView: (gameId: string) => void;
}) {
  if (games.length === 0) {
    return (
      <p className="history-empty">
        {state.historyPending ? '正在读取历史记录…' : (emptyMessage ?? '暂无已完成对局。')}
      </p>
    );
  }
  return (
    <div className="history-list">
      {games.map((item) => {
        const black = item.players.find((player) => player.color === 'BLACK');
        const white = item.players.find((player) => player.color === 'WHITE');
        return (
          <article className="history-row" key={item.gameId}>
            <span className="history-result">{item.finalResult ?? '中止'}</span>
            <span className="history-players">
              <strong>
                黑 · {black?.displayName ?? '—'} / 白 · {white?.displayName ?? '—'}
              </strong>
              <small>
                {item.boardSize} 路 · {item.currentSequence} 手 ·{' '}
                {new Date(item.finishedAt).toLocaleString('zh-CN')}
              </small>
            </span>
            <span className="history-actions">
              <button type="button" onClick={() => onView(item.gameId)}>
                查看
              </button>
              <button
                type="button"
                disabled={item.status !== 'FINISHED' || state.downloadPendingGameId === item.gameId}
                onClick={() => void game.downloadSgf(item.gameId)}
              >
                {state.downloadPendingGameId === item.gameId ? '下载中…' : '下载 SGF'}
              </button>
            </span>
          </article>
        );
      })}
    </div>
  );
}

export function GameRoom({
  game,
  activity,
  membershipId,
  role,
  onReview,
}: {
  readonly game: GameStore;
  readonly activity?: StudentActivityApi | undefined;
  readonly membershipId: string;
  readonly role: string;
  readonly onReview?: ((gameId: string) => void) | undefined;
}) {
  const playerWorkspace = role === 'STUDENT' || role === 'TEACHER';
  const state = useSyncExternalStore(game.subscribe, game.snapshot);
  const [selectedDispute, setSelectedDispute] = useState<ReadonlySet<string>>(new Set());
  const [moveDraft, setMoveDraft] = useState<MoveDraft | null>(null);
  const [confirmMoves, setConfirmMoves] = useState(true);
  const [copyError, setCopyError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [prompt, setPrompt] = useState<GamePrompt | null>(null);
  const pageRef = useRef<HTMLElement>(null);
  const clockVoiceMemory = useRef(initialClockVoiceMemory());
  const monotonicTime = useMonotonicNow();
  const snapshot = state.snapshot;
  const roomMembers = useMemo(
    () =>
      snapshot?.room.players.map((player) => ({
        membershipId: player.membershipId,
        displayName: player.displayName,
        role: player.seat,
        online: state.connection === 'online' && player.membershipId === membershipId,
      })) ?? [],
    [snapshot, state.connection, membershipId],
  );
  const me = snapshot?.room.players.find((player) => player.membershipId === membershipId);
  const myColor = me?.seat ?? null;
  const scoringProposal = snapshot?.scoring?.proposal;
  const myMarksSubmitted =
    myColor === 'BLACK'
      ? scoringProposal?.submittedBy.blackAt != null
      : myColor === 'WHITE'
        ? scoringProposal?.submittedBy.whiteAt != null
        : false;
  const clockAnchor = useMemo(
    () =>
      snapshot === null ? null : createClockProjectionAnchor(snapshot.timing.asOf, monotonicNow()),
    [snapshot?.gameId, snapshot?.timing.asOf],
  );
  const now = clockAnchor === null ? Date.now() : projectedServerNow(clockAnchor, monotonicTime);

  useEffect(() => {
    setSelectedDispute(new Set());
  }, [snapshot?.gameId]);

  useEffect(() => {
    if (shouldClearScoringDraft(snapshot?.status, myMarksSubmitted)) {
      setSelectedDispute(new Set());
    }
  }, [myMarksSubmitted, snapshot?.status]);

  useEffect(() => {
    setPrompt(null);
  }, [snapshot?.gameId, snapshot?.status]);

  useEffect(() => {
    if (state.error === null || playerWorkspace) return;
    const timer = window.setTimeout(() => game.clearError(), GAME_ERROR_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [game, playerWorkspace, state.error]);

  useEffect(() => {
    const synchronizeFullscreen = () => {
      if (document.fullscreenElement === null) setImmersive(false);
      else setImmersive(document.fullscreenElement === pageRef.current);
    };
    document.addEventListener('fullscreenchange', synchronizeFullscreen);
    return () => document.removeEventListener('fullscreenchange', synchronizeFullscreen);
  }, []);

  useEffect(() => {
    cancelClockAnnouncements();
    clockVoiceMemory.current = initialClockVoiceMemory();
  }, [membershipId, snapshot?.gameId]);

  useEffect(() => {
    if (
      snapshot === null ||
      snapshot.status !== 'PLAYING' ||
      snapshot.timing.status !== 'RUNNING'
    ) {
      return;
    }
    const blackByoyomi = projectedByoyomi(snapshot.timing, 'BLACK', now);
    const whiteByoyomi = projectedByoyomi(snapshot.timing, 'WHITE', now);
    const perspectiveColor =
      snapshot.room.players.find((player) => player.membershipId === membershipId)?.seat ?? null;
    const voice = advanceClockVoice(clockVoiceMemory.current, {
      gameId: snapshot.gameId,
      perspectiveColor,
      mainTimeMs: snapshot.timing.mainTimeMs,
      byoyomiConfigured: snapshot.timing.byoyomi !== null && snapshot.timing.byoyomi !== undefined,
      activeColor: snapshot.timing.activeColor,
      turnStartedAt: snapshot.timing.turnStartedAt,
      blackMainRemainingMs: projectedRemainingMs(snapshot.timing, 'BLACK', now),
      whiteMainRemainingMs: projectedRemainingMs(snapshot.timing, 'WHITE', now),
      blackByoyomi,
      whiteByoyomi,
      blackPeriods: blackByoyomi?.periods ?? snapshot.timing.black.byoyomiPeriodsRemaining ?? null,
      whitePeriods: whiteByoyomi?.periods ?? snapshot.timing.white.byoyomiPeriodsRemaining ?? null,
    });
    clockVoiceMemory.current = voice.memory;
    announceClock(voice.announcements);
  }, [membershipId, now, snapshot]);

  useEffect(() => () => cancelClockAnnouncements(), []);

  const paused = snapshot?.supervision?.pause != null;
  const disconnected = snapshot?.disconnection != null;
  const canPlay =
    snapshot?.status === 'PLAYING' &&
    snapshot.board.currentPlayer === myColor &&
    state.connection === 'online' &&
    state.pendingCommand === null &&
    !paused &&
    !disconnected;
  const draftPoint = snapshot === null ? null : currentMoveDraft(moveDraft, snapshot, canPlay);
  useEffect(() => {
    setMoveDraft(null);
  }, [snapshot?.gameId, snapshot?.stateVersion, canPlay, confirmMoves]);
  useEffect(() => {
    if (snapshot === null || !unfinishedGame(snapshot)) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [snapshot?.gameId, snapshot?.status]);

  const scoringMode = snapshot?.status === 'SCORING';
  const handleBoardPoint = useCallback(
    (point: BoardPoint) => {
      if (snapshot === null) return;
      if (!scoringMode) {
        if (!canPlay) return;
        if (playerWorkspace && confirmMoves) {
          setMoveDraft({ gameId: snapshot.gameId, stateVersion: snapshot.stateVersion, point });
        } else game.play(point);
        return;
      }
      const stone = snapshot.board.stones[point.y * snapshot.board.size + point.x];
      const targetColor = myColor === 'BLACK' ? 'WHITE' : myColor === 'WHITE' ? 'BLACK' : null;
      if (stone !== targetColor || myMarksSubmitted) return;
      const key = pointKey(point);
      setSelectedDispute((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [
      snapshot,
      scoringMode,
      canPlay,
      confirmMoves,
      game,
      myColor,
      myMarksSubmitted,
      playerWorkspace,
    ],
  );

  if (snapshot === null) return null;
  const boardDisabled =
    state.connection !== 'online' ||
    state.pendingCommand !== null ||
    paused ||
    disconnected ||
    (snapshot.status === 'PLAYING' ? !canPlay : snapshot.status !== 'SCORING' || myMarksSubmitted);
  const interactionNotice =
    state.connection !== 'online'
      ? '连接恢复中，请稍候'
      : disconnected
        ? '断线保护中，对局已暂停'
        : paused
          ? '老师已暂停本局'
          : state.pendingCommand !== null
            ? '正在提交，请稍候…'
            : null;
  const confirmMove = () => {
    const latest = game.snapshot();
    const point =
      latest.snapshot === null
        ? null
        : currentMoveDraft(
            moveDraft,
            latest.snapshot,
            canPlay && latest.connection === 'online' && latest.pendingCommand === null,
          );
    if (point !== null) game.play(point);
    setMoveDraft(null);
  };
  const selectedPoints = [...selectedDispute].map((key) => {
    const [x, y] = key.split(':').map(Number);
    return { x: x ?? 0, y: y ?? 0 };
  });

  const countingRequest = snapshot.scoring?.countingRequest;
  const serverPrompt: GamePrompt | null =
    snapshot.status === 'COUNTING_REQUESTED' &&
    countingRequest !== null &&
    countingRequest !== undefined &&
    countingRequest.responder === myColor
      ? { kind: 'RESPOND_COUNTING', countingRequestId: countingRequest.countingRequestId }
      : state.activeTakeback?.responderMembershipId === membershipId
        ? { kind: 'RESPOND_TAKEBACK', sequence: state.activeTakeback.targetMove.sequence }
        : null;
  const activePrompt = serverPrompt ?? prompt;

  const resolvePrompt = (accepted: boolean) => {
    const current = activePrompt;
    if (current === null) return;
    if (current.kind === 'RESPOND_COUNTING') {
      game.respondCounting(current.countingRequestId, accepted);
      return;
    }
    if (current.kind === 'RESPOND_TAKEBACK') {
      void game.respondTakeback(accepted);
      return;
    }
    setPrompt(null);
    if (!accepted) return;
    if (current.kind === 'LEAVE') game.leave();
    else if (current.kind === 'PASS') game.pass();
    else if (current.kind === 'RESIGN') game.resign();
    else game.proposeScoring(current.points);
  };

  const copyRoomCode = () => {
    void Promise.resolve()
      .then(() => navigator.clipboard.writeText(snapshot.room.roomCode))
      .then(() => {
        setCopyError(false);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1_500);
      })
      .catch(() => setCopyError(true));
  };

  const toggleFullscreen = async () => {
    const page = pageRef.current;
    if (page === null) return;
    if (document.fullscreenElement === page) {
      await document.exitFullscreen();
      return;
    }
    if (immersive && document.fullscreenElement === null) {
      setImmersive(false);
      return;
    }
    try {
      await page.requestFullscreen();
      setImmersive(true);
    } catch {
      setImmersive(true);
    }
  };

  const gameHeading = (
    <section className="game-heading" aria-label="对局信息">
      <div className="game-heading-main">
        <button
          className="back-button return-button"
          type="button"
          onClick={() => (unfinishedGame(snapshot) ? setPrompt({ kind: 'LEAVE' }) : game.leave())}
        >
          {playerWorkspace ? <ArrowLeftIcon aria-hidden="true" /> : '← '}
          返回大厅
        </button>
        <div className="game-title-line">
          <h1>
            {snapshot.room.boardSize} 路 · {gameTypeLabel(snapshot.room.handicap)}
          </h1>
          <span className={`status-pill status-pill--${snapshot.status.toLowerCase()}`}>
            {STATUS_LABELS[snapshot.status]}
          </span>
        </div>
        {!playerWorkspace ? <p>对局 ID · {snapshot.gameId}</p> : null}
      </div>
      <div className="game-heading-actions">
        <button
          className="fullscreen-button fullscreen-action"
          type="button"
          onClick={() => void toggleFullscreen()}
        >
          {playerWorkspace ? (
            immersive ? (
              <ExitFullScreenIcon aria-hidden="true" />
            ) : (
              <EnterFullScreenIcon aria-hidden="true" />
            )
          ) : (
            <span aria-hidden="true">{immersive ? '↙' : '↗'}</span>
          )}
          {immersive ? (
            '退出全屏'
          ) : playerWorkspace ? (
            <span className="fullscreen-enter-label">进入全屏</span>
          ) : (
            '进入全屏'
          )}
        </button>
        {immersive ? (
          <span className="immersive-game-title">
            {snapshot.room.boardSize} 路 · {gameTypeLabel(snapshot.room.handicap)}
          </span>
        ) : (
          <button className="room-code-chip" type="button" onClick={copyRoomCode}>
            <span>房间码</span>
            <strong>{snapshot.room.roomCode}</strong>
            <small>{copyError ? '请手动复制房间码' : copied ? '已复制' : '点击复制'}</small>
          </button>
        )}
      </div>
    </section>
  );
  const pauseNotice = paused ? (
    <p className="message message--warning">教师已暂停本局，棋钟和落子均已停止。</p>
  ) : null;
  const moveSummary = (
    <section className="move-summary" aria-label="对局手数">
      <span>当前手数</span>
      <strong>{snapshot.currentSequence}</strong>
      <span>连续停一手</span>
      <strong>{snapshot.consecutivePasses}</strong>
    </section>
  );
  return (
    <main
      ref={pageRef}
      className={`${playerWorkspace ? 'board-workspace student-play-workspace ' : ''}${role === 'TEACHER' ? 'teacher-game-page ' : ''}${immersive ? 'game-page game-page--immersive' : 'game-page'}`}
    >
      {!playerWorkspace ? (
        <>
          {gameHeading}
          {pauseNotice}
        </>
      ) : null}
      <section className="game-layout">
        {playerWorkspace ? (
          <aside className="student-game-context" aria-label="对局概况">
            {gameHeading}
            <StudentRoomMembersCard
              api={activity}
              gameId={snapshot.gameId}
              initialMembers={roomMembers}
              collapsible
            />
            <ConnectionCard
              connection={state.connection}
              stateVersion={snapshot.stateVersion}
              compact
            />
            {pauseNotice}
          </aside>
        ) : null}
        <div className="board-column">
          <GoBoard
            boardAppearance={playerWorkspace ? 'wood' : 'classic'}
            snapshot={snapshot}
            keyboardNavigation={playerWorkspace}
            previewPoint={draftPoint}
            disabled={boardDisabled}
            scoringMode={scoringMode}
            scoringTargetColor={
              myColor === 'BLACK' ? 'WHITE' : myColor === 'WHITE' ? 'BLACK' : null
            }
            selectedDisputedStones={selectedDispute}
            lastMove={state.lastMove}
            onPoint={handleBoardPoint}
          />
          <RoomInvitation
            game={game}
            snapshot={snapshot}
            membershipId={membershipId}
            onCopy={copyRoomCode}
            copied={copied}
            copyError={copyError}
          />
          <NigiriOverlay
            game={game}
            snapshot={snapshot}
            membershipId={membershipId}
            now={now}
            pending={state.pendingCommand !== null}
          />
          <DisconnectionCountdown snapshot={snapshot} now={now} myColor={myColor} />
          <GamePromptOverlay
            prompt={activePrompt}
            pending={state.pendingCommand !== null}
            onCancel={() => resolvePrompt(false)}
            onConfirm={() => resolvePrompt(true)}
          />
          {state.error !== null ? (
            <div
              className="message message--error game-message game-message--overlay"
              role="status"
              aria-live="assertive"
            >
              <span>{state.error}</span>
              {playerWorkspace ? (
                <button type="button" onClick={() => game.clearError()}>
                  关闭提示
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <aside className="game-sidebar">
          <section className="player-panel" aria-label="对局双方">
            <PlayerBar color="WHITE" snapshot={snapshot} now={now} membershipId={membershipId} />
            <PlayerBar color="BLACK" snapshot={snapshot} now={now} membershipId={membershipId} />
          </section>
          {!playerWorkspace ? (
            <ConnectionCard connection={state.connection} stateVersion={snapshot.stateVersion} />
          ) : null}
          <ScoringResultDialog
            game={game}
            snapshot={snapshot}
            myColor={myColor}
            pending={state.pendingCommand !== null}
          />
          {!playerWorkspace || snapshot.terminal === undefined ? (
            <GameActions
              game={game}
              snapshot={snapshot}
              myColor={myColor}
              membershipId={membershipId}
              pending={
                state.pendingCommand !== null ||
                state.connection !== 'online' ||
                paused ||
                disconnected
              }
              playerWorkspace={playerWorkspace}
              interactionNotice={interactionNotice}
              onRequestPass={() => setPrompt({ kind: 'PASS' })}
              moveControls={
                playerWorkspace && snapshot.status === 'PLAYING' ? (
                  <div className="move-confirmation-controls">
                    <label>
                      <input
                        type="checkbox"
                        checked={confirmMoves}
                        disabled={state.pendingCommand !== null}
                        onChange={(event) => setConfirmMoves(event.target.checked)}
                      />
                      先选点，再确认落子
                    </label>
                    {confirmMoves ? (
                      <div className="move-draft-actions">
                        <span aria-live="polite">
                          {draftPoint !== null
                            ? `已选 ${boardPointLabel(draftPoint, snapshot.board.size)}`
                            : canPlay
                              ? '请先在棋盘上选点'
                              : '等待可落子时再选点'}
                        </span>
                        <button
                          type="button"
                          disabled={draftPoint === null}
                          onClick={() => setMoveDraft(null)}
                        >
                          取消选点
                        </button>
                        <button
                          type="button"
                          className="primary-button"
                          disabled={draftPoint === null}
                          onClick={confirmMove}
                        >
                          确认落子
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null
              }
              activeTakeback={state.activeTakeback}
              selectedPoints={selectedPoints}
              onRequestResign={() => setPrompt({ kind: 'RESIGN' })}
              onRequestScoringSubmission={(points) =>
                setPrompt({
                  kind: 'SUBMIT_SCORING',
                  points: points.map((point) => ({ ...point })),
                })
              }
            />
          ) : null}
          {snapshot.terminal !== undefined ? (
            <ResultCard
              snapshot={snapshot}
              myColor={myColor}
              onReview={onReview}
              reportPending={state.reportPending}
              downloadPending={state.downloadPendingGameId === snapshot.gameId}
              onReport={() => void game.loadTimingReport()}
              onDownload={() => void game.downloadSgf()}
            />
          ) : null}
          {!playerWorkspace ? moveSummary : null}
        </aside>
      </section>
    </main>
  );
}

function DisconnectionCountdown({
  snapshot,
  now,
  myColor,
}: {
  readonly snapshot: TimedGameSnapshot;
  readonly now: number;
  readonly myColor: StoneColor | null;
}) {
  const disconnection = snapshot.disconnection;
  if (disconnection === null || disconnection === undefined) return null;
  const remainingSeconds = Math.max(
    0,
    Math.ceil((Date.parse(disconnection.deadlineAt) - now) / 1_000),
  );
  const blackOffline = disconnection.blackDisconnectedAt !== null;
  const whiteOffline = disconnection.whiteDisconnectedAt !== null;
  const bothOffline = blackOffline && whiteOffline;
  const ownOffline = (myColor === 'BLACK' && blackOffline) || (myColor === 'WHITE' && whiteOffline);
  return (
    <div className="disconnect-backdrop" role="presentation">
      <section className="disconnect-card" role="dialog" aria-modal="true" aria-label="断线等待">
        <p className="eyebrow">断线保护</p>
        <h2>{bothOffline ? '双方均已离线' : ownOffline ? '正在恢复你的连接' : '对方暂时离线'}</h2>
        <strong>{remainingSeconds}</strong>
        <p>
          {bothOffline
            ? '倒计时不会重置；截止时双方仍未重连，本局判和。'
            : '对局与棋钟已暂停；截止时离线方仍未返回，将自动判负。'}
        </p>
      </section>
    </div>
  );
}

function ScoringResultDialog({
  game,
  snapshot,
  myColor,
  pending,
}: {
  readonly game: GameStore;
  readonly snapshot: TimedGameSnapshot;
  readonly myColor: StoneColor | null;
  readonly pending: boolean;
}) {
  const proposal = snapshot.scoring?.proposal;
  if (
    snapshot.status !== 'SCORING' ||
    proposal === null ||
    proposal === undefined ||
    proposal.submittedBy.blackAt == null ||
    proposal.submittedBy.whiteAt == null
  ) {
    return null;
  }
  const myConfirmed =
    myColor === 'BLACK'
      ? proposal.confirmedBy.blackAt != null
      : myColor === 'WHITE'
        ? proposal.confirmedBy.whiteAt != null
        : false;
  return (
    <section
      className="scoring-result-dialog"
      role="dialog"
      aria-modal="false"
      aria-label="最终数子确认"
    >
      <p className="eyebrow">最终数子确认</p>
      <h2>{proposal.finalResult}</h2>
      <p>双方都确认才维持本次判定；任一方选择继续对弈，服务器会恢复数子前盘面。</p>
      <div className="game-prompt-actions">
        <button
          type="button"
          disabled={pending || myColor === null}
          onClick={() => game.rejectScoring(proposal.scoringVersion, proposal.scoringHash)}
        >
          继续对弈
        </button>
        <button
          className="primary-button"
          type="button"
          disabled={pending || myColor === null || myConfirmed}
          onClick={() => game.confirmScoring(proposal.scoringVersion, proposal.scoringHash)}
        >
          {scoringConfirmationLabel(myConfirmed)}
        </button>
      </div>
    </section>
  );
}

function GamePromptOverlay({
  prompt,
  pending,
  onCancel,
  onConfirm,
}: {
  readonly prompt: GamePrompt | null;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  if (prompt === null) return null;
  const key =
    prompt.kind === 'RESPOND_COUNTING'
      ? prompt.countingRequestId
      : prompt.kind === 'RESPOND_TAKEBACK'
        ? String(prompt.sequence)
        : prompt.kind;
  return (
    <GameConfirmationDialog
      key={key}
      content={gamePromptContent(prompt)}
      pending={pending}
      dismissible={prompt.kind !== 'RESPOND_COUNTING' && prompt.kind !== 'RESPOND_TAKEBACK'}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

function NigiriOverlay({
  game,
  snapshot,
  membershipId,
  now,
  pending,
}: {
  readonly game: GameStore;
  readonly snapshot: TimedGameSnapshot;
  readonly membershipId: string;
  readonly now: number;
  readonly pending: boolean;
}) {
  const nigiri = snapshot.nigiri;
  const [dismissedResolution, setDismissedResolution] = useState<string | null>(null);

  useEffect(() => {
    if (nigiri?.stage !== 'RESOLVED' || nigiri.resolvedAt === null) return;
    const key = `${snapshot.gameId}:${nigiri.resolvedAt}`;
    const timer = window.setTimeout(() => setDismissedResolution(key), 3_000);
    return () => window.clearTimeout(timer);
  }, [nigiri?.resolvedAt, nigiri?.stage, snapshot.gameId]);

  if (nigiri === undefined) return null;
  const resolutionKey = `${snapshot.gameId}:${nigiri.resolvedAt ?? ''}`;
  if (nigiri.stage === 'RESOLVED' && dismissedResolution === resolutionKey) return null;

  const isSelector = membershipId === nigiri.selectorMembershipId;
  const isGuesser = membershipId === nigiri.guesserMembershipId;
  if (!isSelector && !isGuesser) return null;
  const mySeat = snapshot.room.players.find((player) => player.membershipId === membershipId)?.seat;
  const remainingMs =
    nigiri.deadlineAt === null ? 0 : Math.max(0, Date.parse(nigiri.deadlineAt) - now);
  const progress = Math.max(0, Math.min(100, (remainingMs / 10_000) * 100));

  if (nigiri.stage === 'RESOLVED') {
    const verdict =
      isGuesser && nigiri.guesserCorrect !== null
        ? nigiri.guesserCorrect
          ? '正确'
          : '错误'
        : nigiri.resolution === 'SELECTOR_TIMEOUT'
          ? isSelector
            ? '选择超时'
            : '对方选择超时'
          : nigiri.resolution === 'GUESSER_TIMEOUT'
            ? isGuesser
              ? '判断超时'
              : '对方判断超时'
            : '猜先完成';
    return (
      <section className="nigiri-overlay" role="status" aria-live="assertive">
        <p className="eyebrow">猜先结果</p>
        <strong className="nigiri-verdict">{verdict}</strong>
        <h2>{mySeat === 'BLACK' ? '开始对弈，执黑先行' : '开始对弈，执白后行'}</h2>
      </section>
    );
  }

  const waitingForSelection = nigiri.stage === 'SELECTING' && isGuesser;
  const waitingForGuess = nigiri.stage === 'GUESSING' && isSelector;
  return (
    <section className="nigiri-overlay" role="dialog" aria-modal="true" aria-label="猜先">
      <p className="eyebrow">NIGIRI · 猜先</p>
      <h2>
        {waitingForSelection
          ? '等待对方选定'
          : waitingForGuess
            ? '等待对方猜先结果'
            : nigiri.stage === 'SELECTING'
              ? '请选择一个数字'
              : '请选择单数或双数'}
      </h2>
      {!waitingForSelection && !waitingForGuess && nigiri.stage === 'SELECTING' ? (
        <div className="nigiri-number-grid">
          {Array.from({ length: 16 }, (_, index) => index + 1).map((number) => (
            <button
              key={number}
              type="button"
              disabled={pending || remainingMs <= 0}
              onClick={() => game.selectNigiri(number)}
            >
              {number}
            </button>
          ))}
        </div>
      ) : null}
      {!waitingForSelection && !waitingForGuess && nigiri.stage === 'GUESSING' ? (
        <div className="nigiri-parity-grid">
          <button
            type="button"
            disabled={pending || remainingMs <= 0}
            onClick={() => game.guessNigiri('ODD')}
          >
            单数
          </button>
          <button
            type="button"
            disabled={pending || remainingMs <= 0}
            onClick={() => game.guessNigiri('EVEN')}
          >
            双数
          </button>
        </div>
      ) : null}
      <div className="nigiri-countdown" aria-label={`剩余 ${Math.ceil(remainingMs / 1_000)} 秒`}>
        <div style={{ '--nigiri-progress': `${progress}%` } as CSSProperties} />
      </div>
      <small>{Math.ceil(remainingMs / 1_000)}s</small>
    </section>
  );
}

export function PlayerBar({
  color,
  snapshot,
  now,
  membershipId,
}: {
  readonly color: StoneColor;
  readonly snapshot: TimedGameSnapshot;
  readonly now: number;
  readonly membershipId?: string | undefined;
}) {
  const player = snapshot.room.players.find((candidate) => candidate.seat === color);
  const active = snapshot.status === 'PLAYING' && snapshot.board.currentPlayer === color;
  const byoyomi = projectedByoyomi(snapshot.timing, color, now);
  return (
    <section className={`player-bar ${active ? 'player-bar--active' : ''}`}>
      <span className={`player-stone player-stone--${color.toLowerCase()}`} aria-hidden="true" />
      <div className="player-name">
        <small className="player-perspective">
          {membershipId === undefined
            ? '棋手'
            : player?.membershipId === membershipId
              ? '你'
              : '对手'}{' '}
          · {color === 'BLACK' ? '执黑' : '执白'}
        </small>
        <strong>{player?.displayName ?? '等待入座'}</strong>
      </div>
      <span className={`ready-mark ${player?.ready === true ? 'is-ready' : ''}`}>
        {snapshot.status === 'PLAYING'
          ? snapshot.supervision?.pause != null || snapshot.disconnection != null
            ? '已暂停'
            : active
              ? '思考中'
              : '等待对手'
          : snapshot.status === 'FINISHED' || snapshot.status === 'ABORTED'
            ? '已结束'
            : snapshot.status === 'SCORING' || snapshot.status === 'COUNTING_REQUESTED'
              ? '数子中'
              : player?.ready === true
                ? '已准备'
                : '未准备'}
      </span>
      <time className={active ? 'clock clock--active' : 'clock'}>
        {byoyomi === null
          ? formatDuration(projectedRemainingMs(snapshot.timing, color, now))
          : `${Math.ceil(byoyomi.periodRemainingMs / 1_000)}s · ${byoyomi.periods}次`}
      </time>
    </section>
  );
}

function ConnectionCard({
  connection,
  stateVersion,
  compact = false,
}: {
  readonly connection: string;
  readonly stateVersion: number;
  readonly compact?: boolean;
}) {
  const online = connection === 'online';
  return (
    <section className="connection-card">
      <span className={online ? 'connection-dot is-online' : 'connection-dot'} />
      <span>
        {online ? '实时连接正常' : connection === 'reconnecting' ? '正在恢复连接' : '正在连接'}
      </span>
      {!compact ? <small>状态版本 v{stateVersion}</small> : null}
    </section>
  );
}

function GameActions({
  game,
  snapshot,
  myColor,
  membershipId,
  pending,
  activeTakeback,
  selectedPoints,
  playerWorkspace = false,
  interactionNotice,
  moveControls,
  onRequestPass,
  onRequestResign,
  onRequestScoringSubmission,
}: {
  readonly game: GameStore;
  readonly snapshot: TimedGameSnapshot;
  readonly myColor: StoneColor | null;
  readonly membershipId: string;
  readonly pending: boolean;
  readonly activeTakeback: ReturnType<GameStore['snapshot']>['activeTakeback'];
  readonly selectedPoints: readonly BoardPoint[];
  readonly playerWorkspace?: boolean;
  readonly interactionNotice?: string | null;
  readonly moveControls?: ReactNode;
  readonly onRequestPass?: () => void;
  readonly onRequestResign: () => void;
  readonly onRequestScoringSubmission: (points: readonly BoardPoint[]) => void;
}) {
  const me = snapshot.room.players.find((player) => player.membershipId === membershipId);
  const countingRequest = snapshot.scoring?.countingRequest;
  const proposal = snapshot.scoring?.proposal;

  if (
    snapshot.status === 'CREATED' ||
    snapshot.status === 'WAITING' ||
    snapshot.status === 'READY'
  ) {
    return (
      <section className="action-card">
        <p className="eyebrow">开局准备</p>
        <h2>{snapshot.room.players.length < 2 ? '等待对手加入' : '双方确认后开局'}</h2>
        <p>
          {snapshot.room.players.length < 2
            ? '把房间码分享给另一位玩家。'
            : '准备状态由服务器同步给双方。'}
        </p>
        <button
          className="primary-button primary-button--wide"
          type="button"
          disabled={pending || me?.ready === true || snapshot.room.players.length < 2}
          onClick={() => game.ready()}
        >
          {me?.ready === true ? '我已准备' : '确认准备'}
        </button>
      </section>
    );
  }

  if (snapshot.status === 'PLAYING') {
    const myTurn = snapshot.board.currentPlayer === myColor;
    return (
      <section className="action-card">
        <h2 aria-live="polite">{interactionNotice ?? (myTurn ? '轮到你落子' : '等待对方落子')}</h2>
        <p className="game-turn-help">
          {playerWorkspace
            ? myTurn
              ? '在交叉点落子，想好再下。'
              : '对手正在思考，请稍候。'
            : '点击棋盘交叉点落子。'}
        </p>
        {moveControls}
        <div className="action-grid">
          <button
            type="button"
            disabled={pending || !myTurn}
            onClick={() =>
              playerWorkspace && onRequestPass !== undefined ? onRequestPass() : game.pass()
            }
          >
            停一手
          </button>
          <button type="button" disabled={pending} onClick={() => game.requestCounting()}>
            申请数子
          </button>
          <button
            type="button"
            disabled={pending || snapshot.currentSequence === 0 || activeTakeback !== null}
            onClick={() => void game.requestTakeback()}
          >
            申请悔棋
          </button>
          <button
            className="danger-action"
            type="button"
            disabled={pending}
            onClick={onRequestResign}
          >
            认输
          </button>
        </div>
        {activeTakeback !== null && activeTakeback.responderMembershipId !== membershipId ? (
          <div className="request-box">
            <strong>悔棋申请 · 第 {activeTakeback.targetMove.sequence} 手</strong>
            <p>等待对方处理。</p>
          </div>
        ) : null}
      </section>
    );
  }

  if (
    snapshot.status === 'COUNTING_REQUESTED' &&
    countingRequest !== undefined &&
    countingRequest !== null
  ) {
    const canRespond = countingRequest.responder === myColor;
    return (
      <section className="action-card">
        <p className="eyebrow">数子请求</p>
        <h2>{canRespond ? '请处理对方的数子请求' : '等待对方确认'}</h2>
        <p>
          {canRespond
            ? '请在棋盘中央选择继续对弈或确定数子。'
            : '对方确认后，服务器将同步进入数子或恢复对弈。'}
        </p>
      </section>
    );
  }

  if (snapshot.status === 'SCORING') {
    const blackSubmitted = proposal?.submittedBy.blackAt != null;
    const whiteSubmitted = proposal?.submittedBy.whiteAt != null;
    const mySubmitted = myColor === 'BLACK' ? blackSubmitted : whiteSubmitted;
    const bothSubmitted = blackSubmitted && whiteSubmitted;
    const blackConfirmed = proposal?.confirmedBy.blackAt != null;
    const whiteConfirmed = proposal?.confirmedBy.whiteAt != null;
    const targetLabel = myColor === 'BLACK' ? '白棋' : '黑棋';
    return (
      <section className="action-card">
        <ol className="game-scoring-steps" aria-label="数子进度">
          <li aria-current={!mySubmitted ? 'step' : undefined}>标记死子</li>
          <li aria-current={mySubmitted && !bothSubmitted ? 'step' : undefined}>双方提交</li>
          <li aria-current={bothSubmitted ? 'step' : undefined}>核对结果</li>
        </ol>
        <h2>
          {bothSubmitted
            ? `数子结果：${proposal?.finalResult ?? '计算中'}`
            : mySubmitted
              ? '本方已提交，等待对方'
              : `请标记盘上的${targetLabel}死子`}
        </h2>
        <p>
          {bothSubmitted
            ? '双方都确认才会维持本次判定；任一方选择继续对弈，服务器将恢复数子前的原盘面。'
            : '执黑方只标白棋死子，执白方只标黑棋死子；双方提交后再共同确认最终结果。'}
        </p>
        <div className="scoring-submission-status" aria-label="双方死子标记提交状态">
          <span className={blackSubmitted ? 'is-submitted' : ''}>
            黑方 · {blackSubmitted ? '已提交' : '待提交'}
          </span>
          <span className={whiteSubmitted ? 'is-submitted' : ''}>
            白方 · {whiteSubmitted ? '已提交' : '待提交'}
          </span>
        </div>
        {!bothSubmitted && !mySubmitted ? (
          <>
            <p className="scoring-selection-count">
              {selectedPoints.length === 0
                ? `尚未选择${targetLabel}死子；若没有死子可直接提交`
                : `已选择 ${selectedPoints.length} 个棋块标记点`}
            </p>
            <button
              className="primary-button primary-button--wide"
              type="button"
              disabled={pending || myColor === null}
              onClick={() => onRequestScoringSubmission(selectedPoints)}
            >
              确认提交死子标记
            </button>
          </>
        ) : null}
        {bothSubmitted && proposal !== null && proposal !== undefined ? (
          <>
            <div className="scoring-submission-status" aria-label="双方最终结果确认状态">
              <span className={blackConfirmed ? 'is-submitted' : ''}>
                黑方 · {blackConfirmed ? '已确认结果' : '待确认结果'}
              </span>
              <span className={whiteConfirmed ? 'is-submitted' : ''}>
                白方 · {whiteConfirmed ? '已确认结果' : '待确认结果'}
              </span>
            </div>
            <p>请在“最终数子确认”中核对结果。有异议可选择继续对弈。</p>
          </>
        ) : null}
        <small>
          点击对方棋子可选择多个棋块；再次点击可取消。提交后不能修改；对结果有异议可在核对阶段继续对弈。
        </small>
      </section>
    );
  }

  return (
    <section className="action-card">
      <p className="eyebrow">对局状态</p>
      <h2>{STATUS_LABELS[snapshot.status]}</h2>
      <p>该状态已由服务器记录。</p>
    </section>
  );
}

function ResultCard({
  snapshot,
  myColor,
  onReview,
  onReport,
  onDownload,
  reportPending,
  downloadPending,
}: {
  readonly snapshot: TimedGameSnapshot;
  readonly myColor?: StoneColor | null;
  readonly onReview?: ((gameId: string) => void) | undefined;
  readonly onReport: () => void;
  readonly onDownload: () => void;
  readonly reportPending: boolean;
  readonly downloadPending: boolean;
}) {
  const terminal = snapshot.terminal;
  if (terminal === undefined) return null;
  return (
    <section className="result-card">
      <p>本局结束</p>
      <h2>
        {terminal.winner === null
          ? '本局和棋'
          : myColor != null
            ? terminal.winner === myColor
              ? '你赢得了本局'
              : '本局惜败'
            : terminal.winner === 'BLACK'
              ? '黑棋胜'
              : '白棋胜'}
      </h2>
      <p>
        {terminal.winner === null ? '双方和棋' : terminal.winner === 'BLACK' ? '黑棋胜' : '白棋胜'}{' '}
        · {resultReason(terminal.resultType)}
      </p>
      <small>棋谱结果：{terminal.finalResult}</small>
      <div className="result-actions">
        {onReview !== undefined ? (
          <button
            className="primary-button primary-button--wide"
            type="button"
            onClick={() => onReview(snapshot.gameId)}
          >
            复盘本局
          </button>
        ) : null}
        {snapshot.timing.ruleCode === 'ABSOLUTE_V1' ? (
          <button
            className="secondary-button secondary-button--wide"
            type="button"
            disabled={reportPending}
            onClick={onReport}
          >
            {reportPending ? '正在生成报告…' : '查看详细用时报告'}
          </button>
        ) : null}
        <button
          className="secondary-button secondary-button--wide"
          type="button"
          disabled={downloadPending}
          onClick={onDownload}
        >
          {downloadPending ? '正在下载…' : '下载 SGF 棋谱'}
        </button>
      </div>
      <p className="game-review-hint">回看一手当时最拿不准的棋，想一想还有什么选择。</p>
    </section>
  );
}

function TimingReportView({
  report,
  moves,
  closeLabel,
  onClose,
}: {
  readonly report: NonNullable<ReturnType<GameStore['snapshot']>['report']>;
  readonly moves: ReturnType<GameStore['snapshot']>['reportMoves'];
  readonly closeLabel: string;
  readonly onClose: () => void;
}) {
  const [requestedFrom, setRequestedFrom] = useState(1);
  const [requestedTo, setRequestedTo] = useState(Math.max(1, report.moveCount));
  const [moveDetailsExpanded, setMoveDetailsExpanded] = useState(true);
  const [moveColorFilter, setMoveColorFilter] = useState<TimingMoveColorFilter>('ALL');
  const [moveDurationFilter, setMoveDurationFilter] = useState<TimingMoveDurationFilter>('ALL');
  useEffect(() => {
    setRequestedFrom(1);
    setRequestedTo(Math.max(1, report.moveCount));
    setMoveColorFilter('ALL');
    setMoveDurationFilter('ALL');
  }, [report.generatedAt, report.moveCount]);
  const range = normalizeTimingRange(report.moveCount, requestedFrom, requestedTo);
  const summary = useMemo(() => summarizeTimingRange(moves, range), [moves, range]);
  const comparisonRows = useMemo(() => buildTimingComparisonRows(summary.moves), [summary.moves]);
  const colorEligibleDetailMoves = useMemo(
    () => filterTimingMoves(summary.moves, moveColorFilter, 'ALL'),
    [moveColorFilter, summary.moves],
  );
  const filteredDetailMoves = useMemo(
    () => filterTimingMoves(colorEligibleDetailMoves, 'ALL', moveDurationFilter),
    [colorEligibleDetailMoves, moveDurationFilter],
  );
  const maximumMoveMs = Math.max(1, ...summary.moves.map((move) => move.elapsedMs ?? 0));

  return (
    <section
      className="timing-report-page"
      role="dialog"
      aria-modal="true"
      aria-label="详细用时报告"
    >
      <header className="timing-report-header">
        <div>
          <p className="eyebrow">AUTHORITATIVE TIMING REPORT</p>
          <h1>详细用时报告</h1>
          <p>
            {report.moveCount === 0 ? '暂无逐手记录' : `第 1–${report.moveCount} 手`} · 服务端生成于{' '}
            {new Date(report.generatedAt).toLocaleString('zh-CN')}
          </p>
        </div>
        <button className="report-close return-button" type="button" onClick={onClose}>
          {closeLabel}
        </button>
      </header>

      <div className="report-overview">
        <article>
          <span>整盘计时</span>
          <strong>{formatDuration(report.timedDurationMs)}</strong>
          <small>共 {report.moveCount} 手</small>
        </article>
        <article>
          <span>黑棋平均</span>
          <strong>{formatPreciseDuration(report.black.averageMoveMs)}</strong>
          <small>最长第 {report.black.longestMove?.sequence ?? '—'} 手</small>
        </article>
        <article>
          <span>白棋平均</span>
          <strong>{formatPreciseDuration(report.white.averageMoveMs)}</strong>
          <small>最长第 {report.white.longestMove?.sequence ?? '—'} 手</small>
        </article>
        <article>
          <span>暂停/非计时</span>
          <strong>{formatDuration(report.pauseDurationMs)}</strong>
          <small>总历时 {formatDuration(report.wallDurationMs)}</small>
        </article>
      </div>

      <section className="range-card">
        <div className="range-heading">
          <div>
            <p className="eyebrow">区间分析</p>
            <h2>选择要比较的手数范围</h2>
          </div>
          <div className="range-inputs">
            <label>
              起始手
              <input
                type="number"
                min={report.moveCount === 0 ? 0 : 1}
                max={report.moveCount}
                value={range.fromSequence}
                disabled={report.moveCount === 0}
                onChange={(event) => setRequestedFrom(Number(event.target.value))}
              />
            </label>
            <span>至</span>
            <label>
              结束手
              <input
                type="number"
                min={report.moveCount === 0 ? 0 : range.fromSequence}
                max={report.moveCount}
                value={range.toSequence}
                disabled={report.moveCount === 0}
                onChange={(event) => setRequestedTo(Number(event.target.value))}
              />
            </label>
          </div>
        </div>

        <div className="range-summary">
          <span>
            区间 {summary.moveCount} 手 · 合计 {formatPreciseDuration(summary.totalMs)} · 平均{' '}
            {formatPreciseDuration(summary.averageMs)}
          </span>
          <span>
            黑 {summary.black.moveCount} 手 / {formatPreciseDuration(summary.black.averageMs)} / 白{' '}
            {summary.white.moveCount} 手 / {formatPreciseDuration(summary.white.averageMs)}
          </span>
          <strong>
            最长思考：第 {summary.longestMove?.sequence ?? '—'} 手 ·{' '}
            {formatPreciseDuration(summary.longestMove?.elapsedMs ?? null)}
          </strong>
        </div>

        <div className="timing-chart" aria-label="所选区间黑白双方逐手用时对比图">
          {summary.moves.length === 0 ? (
            <p>该区间没有逐手记录。</p>
          ) : (
            <>
              <div className="timing-chart-legend" aria-hidden="true">
                <strong className="timing-side-key timing-side-key--black">黑方用时</strong>
                <span>中央分界线</span>
                <strong className="timing-side-key timing-side-key--white">白方用时</strong>
              </div>
              <div className="timing-comparison-rows" role="list">
                {comparisonRows.map((row, index) => {
                  const blackRatio = Math.max(
                    2,
                    ((row.black?.elapsedMs ?? 0) / maximumMoveMs) * 100,
                  );
                  const whiteRatio = Math.max(
                    2,
                    ((row.white?.elapsedMs ?? 0) / maximumMoveMs) * 100,
                  );
                  const rowLabel = [
                    row.black === null
                      ? null
                      : `黑方第 ${row.black.sequence} 手 ${formatPreciseDuration(row.black.elapsedMs)}`,
                    row.white === null
                      ? null
                      : `白方第 ${row.white.sequence} 手 ${formatPreciseDuration(row.white.elapsedMs)}`,
                  ]
                    .filter((value): value is string => value !== null)
                    .join('，');

                  return (
                    <div
                      className="timing-comparison-row"
                      key={`${row.black?.sequence ?? 'none'}-${row.white?.sequence ?? 'none'}-${index}`}
                      role="listitem"
                      aria-label={rowLabel}
                    >
                      <strong className="timing-move-label timing-move-label--black">
                        {row.black === null ? null : `第 ${row.black.sequence} 手`}
                      </strong>
                      <div className="timing-half timing-half--black">
                        {row.black === null ? null : (
                          <>
                            <span>{formatPreciseDuration(row.black.elapsedMs)}</span>
                            <i
                              className="timing-bar timing-bar--black"
                              style={{ '--timing-bar-width': `${blackRatio}%` } as CSSProperties}
                            />
                          </>
                        )}
                      </div>
                      <i className="timing-center-line" aria-hidden="true" />
                      <div className="timing-half timing-half--white">
                        {row.white === null ? null : (
                          <>
                            <i
                              className="timing-bar timing-bar--white"
                              style={{ '--timing-bar-width': `${whiteRatio}%` } as CSSProperties}
                            />
                            <span>{formatPreciseDuration(row.white.elapsedMs)}</span>
                          </>
                        )}
                      </div>
                      <strong className="timing-move-label timing-move-label--white">
                        {row.white === null ? null : `第 ${row.white.sequence} 手`}
                      </strong>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      <section
        className={`move-detail-card${moveDetailsExpanded ? ' is-expanded' : ' is-collapsed'}`}
      >
        <div className="move-detail-heading">
          <div>
            <p className="eyebrow">逐手明细</p>
            <h2>每一手棋的服务端计时记录</h2>
          </div>
          <div className="move-detail-heading__actions">
            <span>精确到毫秒</span>
            <button
              className="collapse-toggle"
              type="button"
              aria-expanded={moveDetailsExpanded}
              onClick={() => setMoveDetailsExpanded((expanded) => !expanded)}
            >
              {moveDetailsExpanded ? '收起' : '展开'}
            </button>
          </div>
        </div>
        {moveDetailsExpanded ? (
          <>
            <div className="move-detail-filters" aria-label="逐手明细筛选">
              <label>
                执棋
                <select
                  value={moveColorFilter}
                  onChange={(event) =>
                    setMoveColorFilter(event.target.value as TimingMoveColorFilter)
                  }
                >
                  <option value="ALL">全部</option>
                  <option value="BLACK">黑棋</option>
                  <option value="WHITE">白棋</option>
                </select>
              </label>
              <label>
                用时
                <select
                  value={moveDurationFilter}
                  onChange={(event) =>
                    setMoveDurationFilter(event.target.value as TimingMoveDurationFilter)
                  }
                >
                  <option value="ALL">全部</option>
                  <option value="OVER_10_SECONDS">本手用时超过10s</option>
                  <option value="OVER_30_SECONDS">本手用时超过30s</option>
                  <option value="OVER_60_SECONDS">本手用时超过60s</option>
                </select>
              </label>
              <span>
                显示 {filteredDetailMoves.length} / {colorEligibleDetailMoves.length} 手
              </span>
            </div>
            <div className="move-table-wrap">
              <table className="move-time-table">
                <thead>
                  <tr>
                    <th>手数</th>
                    <th>执棋</th>
                    <th>类型</th>
                    <th>本手用时</th>
                    <th>该方累计</th>
                    <th>服务器接收时间</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetailMoves.length === 0 ? (
                    <tr>
                      <td className="move-detail-empty" colSpan={6}>
                        没有符合筛选条件的逐手记录。
                      </td>
                    </tr>
                  ) : (
                    filteredDetailMoves.map((move) => (
                      <tr key={move.sequence} className={move.retracted ? 'is-retracted' : ''}>
                        <td>第 {move.sequence} 手</td>
                        <td>
                          <span
                            className={`table-stone table-stone--${move.color.toLowerCase()}`}
                          />
                          {move.color === 'BLACK' ? '黑棋' : '白棋'}
                        </td>
                        <td>{move.kind === 'PASS' ? '停一手' : '落子'}</td>
                        <td>{formatPreciseDuration(move.elapsedMs)}</td>
                        <td>{formatPreciseDuration(move.accumulatedMs)}</td>
                        <td>
                          {new Date(move.moveReceivedAt).toLocaleTimeString('zh-CN', {
                            hour12: false,
                          })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>
    </section>
  );
}

function gameTypeLabel(handicap: number): string {
  return handicap === 0 ? '分先' : handicap === 1 ? '让先' : `让${handicap}子`;
}

function resultReason(
  resultType: 'TIMEOUT' | 'RESIGNATION' | 'SCORE' | 'FORFEIT' | 'DISCONNECTION',
): string {
  const labels = {
    TIMEOUT: '超时判负',
    RESIGNATION: '认输',
    SCORE: '数子终局',
    FORFEIT: '裁定负',
    DISCONNECTION: '断线判定',
  } as const;
  return labels[resultType];
}
